/**
 * ================================================================================
 * AGAPEE JAVASCRIPT UTILITIES
 * ================================================================================
 *
 * Custom JavaScript for the Agapee Paris Shopify theme.
 * All components are wrapped in an IIFE and use 'use strict'.
 *
 * COMPONENTS:
 *
 * 1. Overlay Hover Animation (function)
 *    - Handles opacity and color transitions on hover for grid overlays
 *    - Targets elements with .agp-image_grid__overlay--animate
 *    - Reads data-default-opacity, data-hover-opacity, data-default-color, data-hover-color
 *
 * 2. Parallax Scroll Effect (function)
 *    - Fluid parallax movement for images based on scroll position
 *    - Modes: dynamic-zoom, adaptive-length, clamp
 *    - Respects prefers-reduced-motion
 *    - Targets elements with .agp-image_grid__item--parallax
 *
 * 3. <agp-usp-slider> (Custom Element)
 *    - Horizontal auto-scrolling USP ticker/marquee
 *    - Supports pause-on-hover and reduced motion
 *    - Attributes: data-speed, data-pause-on-hover
 *
 * 4. <agp-accordion> (Custom Element)
 *    - Expand/collapse with smooth height transitions
 *    - Auto-closes siblings (one-open-at-a-time)
 *    - Scroll-on-open with sticky header offset subtraction
 *    - Attributes: data-open-default, data-scroll-on-open, data-scroll-subtract-header
 *
 * 5. <agp-buy-button> (Custom Element)
 *    - Customizable ATC button with dynamic checkout (Shop Pay, Apple Pay, Google Pay)
 *    - Back-in-stock notification via Klaviyo integration
 *    - Max-in-cart detection with custom disabled styling
 *    - Pre-order support
 *    - Listens: variant:change (form), cart:change/cart:refresh/cart:add (document),
 *      theme:loading:start/end (documentElement)
 *    - Dispatches: back-in-stock:open
 *
 * 6. <agp-sticky-atc> (Custom Element)
 *    - Fixed-position add-to-cart bar that appears when main buy button scrolls away
 *    - Supports full and compact layouts (desktop/mobile independently)
 *    - Variant selector sync with main product form
 *    - Max-in-cart, sold-out, pre-order, back-in-stock states
 *    - Deferred form lookup to handle block rendering order
 *    - Hides within last 200px of page
 *    - Moves itself to document.body to escape stacking contexts
 *    - Listens: variant:change (document), cart:change/cart:refresh/cart:add (document)
 *    - Dispatches: cart:refresh, cart:add
 *
 * EVENT ARCHITECTURE:
 * - Theme (Prestige) dispatches cart:change on document.documentElement (bubbles to document)
 * - AgpStickyAtc dispatches cart:refresh and cart:add on document
 * - Both AgpBuyButton and AgpStickyAtc listen on document for all three cart events
 * - variant:change is dispatched on the product form element and bubbles up
 * - AgpStickyAtc listens on document for variant:change, filters by form ID
 *
 * DEPENDENCIES:
 * - Shopify theme (Prestige) for variant:change, cart:change, theme:loading events
 * - Klaviyo onsite.js (optional) for back-in-stock notifications
 * - CSS: assets/agapee.scss (compiled to agapee.css)
 *
 * AUTHOR: Thomas LIMBERT
 * VERSION: 1.3.0
 * ================================================================================
 */

/* ============================================================================
 * Shared: which wallet did Shopify's dynamic checkout serve?
 * ----------------------------------------------------------------------------
 * {{ form | payment_button }} renders <shopify-accelerated-checkout>, which names
 * the wallet both in its `recommended` JSON (shop_pay | apple_pay | google_pay |
 * paypal | buy_it_now) and in the child element it upgrades to
 * (<apple-pay-wallet-button> …). It renders asynchronously, so watch the container
 * until it settles, capped so we never watch forever.
 *
 * agpWatchWallet(container, cb) → cb(name) exactly once; name is null if nothing
 * usable appeared before the cap. Used by <agp-fixed-atc> and <agp-buy-button> to
 * apply the section's per-wallet "hide" switches. Defined here, ABOVE every
 * customElements.define, because defining an element upgrades matching nodes (and
 * runs their connectedCallback) synchronously.
 * ============================================================================ */
if (!window.agpWatchWallet) {
    window.agpWatchWallet = function (box, onDecided) {
        if (!box || typeof onDecided !== 'function') return;
        var mo = null, done = false;
        var finish = function (name) {
            if (done) return;
            done = true;
            if (mo) { mo.disconnect(); mo = null; }
            onDecided(name);
        };
        // Tag / class signature → the same names Shopify uses in `recommended`, so callers can
        // match a wallet exactly (per-wallet hiding) and not just "native or not".
        var nameFrom = function (sig) {
            if (sig.indexOf('apple') > -1) return 'apple_pay';
            if (sig.indexOf('google') > -1) return 'google_pay';
            if (sig.indexOf('shop-pay') > -1 || sig.indexOf('shop_pay') > -1) return 'shop_pay';
            if (sig.indexOf('paypal') > -1) return 'paypal';
            return 'other';
        };
        var decide = function () {
            // The rendered element is ground truth (it is what the shopper sees); `recommended`
            // is the fallback for wallets Shopify renders under an unrecognised tag.
            var kid = box.querySelector('shopify-accelerated-checkout [slot="button"], apple-pay-wallet-button, google-pay-wallet-button, shop-pay-wallet-button, [class*="--apple-pay"], [class*="--google-pay"]');
            if (kid) {
                finish(nameFrom(kid.tagName.toLowerCase() + ' ' + String(kid.className)));
                return true;
            }
            var ac = box.querySelector('shopify-accelerated-checkout');
            if (ac) {
                try {
                    var rec = JSON.parse(ac.getAttribute('recommended') || 'null');
                    if (rec && rec.name) { finish(rec.name); return true; }
                } catch (e) {}
            }
            // "Buy it now" — Shopify's fallback when the device offers no wallet — is drawn as the
            // classic dynamic-checkout button, client-side, so it only exists once it applies.
            if (box.querySelector('.shopify-payment-button__button')) { finish('buy_it_now'); return true; }
            return false;
        };
        if (decide()) return;
        if ('MutationObserver' in window) {
            mo = new MutationObserver(decide);
            mo.observe(box, { childList: true, subtree: true, attributes: true, attributeFilter: ['recommended'] });
        }
        setTimeout(function () { finish(null); }, 5000);
    };
    // Merchant's per-wallet "hide" switches, written by snippets/agp-wallet-buttons.liquid from
    // the section settings. A wallet we could not identify ('other', null) is never hidden — a
    // shopper must always keep a way to check out.
    window.agpWalletHidden = function (name) {
        var cfg = window.agpWalletCfg;
        return !!(cfg && cfg.hide && name && cfg.hide[name] === true);
    };
    // Is any wallet hidden at all? Decides whether the watchers below need arming.
    window.agpAnyWalletHidden = function () {
        var cfg = window.agpWalletCfg;
        if (!cfg || !cfg.hide) return false;
        for (var k in cfg.hide) { if (cfg.hide[k] === true) return true; }
        return false;
    };
}

/* =============================================================================
   agpFitSplitRow — truncation guard for an "Add to cart + express" row
   -----------------------------------------------------------------------------
   Shared by <agp-buy-button> (product info) and <agp-fixed-atc> (sticky bar): both
   place the two buttons on one row at a merchant-chosen split ratio, and a ratio can
   leave either button narrower than its own label needs — the express clips ("Buy with
   sho"), Add to cart wraps — which a translated label makes likelier. Two safeties:

     autoWidth    → when the ratio starves either button, pin the express to its shortest
                    untruncated width and give Add to cart the rest. The express is the
                    side that cannot reflow, so it gets exactly its minimum, no more.
     allowStacked → when even both minimums don't fit on one row, stack instead of cutting.
                    Strictly that no-solution case; without it the express keeps its
                    minimum and the (reflowable) Add-to-cart label wraps.

   Both minimums are measurable even though the wallet lives in a closed shadow DOM:
   `width: max-content` reports its untruncated width. It must be paired with
   `flex: 0 0 auto` — the buttons are flex items and flex-shrink would otherwise squeeze
   the measurement straight back to the width being tested.

   Measurements are taken with the safeties cleared, i.e. from the plain-ratio layout, so
   a pass is idempotent: re-applying the same outcome changes no geometry and the
   observers settle instead of ping-ponging.

   o = { host, row, atc, slot, minClass, stackClass, active, autoWidth, allowStacked }
   ============================================================================= */
if (!window.agpFitSplitRow) {
    window.agpFitSplitRow = function (o) {
        var host = o.host, row = o.row, atc = o.atc, slot = o.slot;
        if (!host || !row || !atc || !slot || host._agpFitting) return;

        host._agpFitting = true;
        try {
            host.classList.remove(o.minClass, o.stackClass);
            slot.style.removeProperty('--agp-express-min');
            if (!o.active || (!o.autoWidth && !o.allowStacked)) return;
            if (getComputedStyle(slot).display === 'none' || !slot.offsetParent) return;

            // Whatever wallet Shopify decided to serve. It is slotted into
            // <shopify-accelerated-checkout> under slot="button" and the TAG DIFFERS per wallet
            // (shop-pay-wallet-button, apple-pay-button, …), so match the slot rather than a tag:
            // naming only Shop Pay meant the guard silently skipped Apple Pay / Google Pay /
            // PayPal and their labels stayed clipped by the slot's overflow: hidden.
            var express = slot.querySelector('shopify-accelerated-checkout [slot="button"]')
                       || slot.querySelector('shop-pay-wallet-button')
                       || slot.querySelector('.shopify-payment-button__button');
            if (!express) return;

            var intrinsic = function (el) {
                var prev = el.getAttribute('style');
                el.style.setProperty('width', 'max-content', 'important');
                el.style.setProperty('flex', '0 0 auto', 'important');
                var w = el.getBoundingClientRect().width;
                if (prev === null) el.removeAttribute('style'); else el.setAttribute('style', prev);
                return w;
            };
            var expressMin = intrinsic(express);
            var atcMin = intrinsic(atc);
            var share = slot.getBoundingClientRect().width;
            var available = row.clientWidth;
            var gap = parseFloat(getComputedStyle(row).columnGap) || 0;
            // A wallet mid-render measures tiny — leave it to the observer's next pass.
            if (expressMin < 40 || !atcMin || !available) return;

            var bothFit = expressMin + gap + atcMin <= available + 0.5;
            var starved = share < expressMin - 0.5 || available - gap - share < atcMin - 0.5;
            if (!starved) return;

            if (o.allowStacked && !bothFit) {
                host.classList.add(o.stackClass);
            } else if (o.autoWidth) {
                // Its label's width, PLUS its share of whatever is left once both labels fit.
                // Pinned to the bare minimum, a wallet's label sits flush against the button's
                // edges — Apple Pay keeps its padding inside a closed shadow DOM, so width is the
                // only lever we have. The leftover is split on the merchant's ratio, so Add to
                // cart keeps its own breathing room too, and a row with no slack still lands
                // exactly on the minimum.
                var ratio = share / Math.max(1, available - gap);
                var slack = Math.max(0, available - gap - expressMin - atcMin);
                slot.style.setProperty('--agp-express-min', Math.ceil(expressMin + slack * ratio) + 'px');
                host.classList.add(o.minClass);
            }
        } finally {
            host._agpFitting = false;
        }
    };
}

(function() {
    'use strict';

    /**
     * Overlay Hover Animation
     * Handles opacity and color transitions on hover for elements with data attributes
     */
    function initOverlayHover() {
        const overlays = document.querySelectorAll('.agp-image_grid__overlay--animate');
        
        overlays.forEach((overlay) => {
            // Skip if already initialized
            if (overlay.dataset.hoverInitialized) return;
            overlay.dataset.hoverInitialized = 'true';

            const item = overlay.closest('.agp-image_grid__item');
            if (!item) return;

            const defaultOpacity = parseFloat(overlay.dataset.defaultOpacity) || 0;
            const hoverOpacity = parseFloat(overlay.dataset.hoverOpacity) || 0.5;
            const defaultColor = overlay.dataset.defaultColor || '';
            const hoverColor = overlay.dataset.hoverColor || '';

            item.addEventListener('mouseenter', () => {
                overlay.style.opacity = hoverOpacity;
                if (hoverColor) {
                    overlay.style.backgroundColor = hoverColor;
                }
            });

            item.addEventListener('mouseleave', () => {
                overlay.style.opacity = defaultOpacity;
                if (defaultColor) {
                    overlay.style.backgroundColor = defaultColor;
                }
            });
        });
    }

    /**
     * Parallax Scroll Effect
     * Applies fluid parallax movement to images based on scroll position.
     * 
     * Three modes available:
     * - dynamic-zoom: Calculates required zoom level to cover max parallax travel
     * - adaptive-length: Adjusts parallax speed based on element position to stay within bounds
     * - clamp: Stops parallax when image border reaches container (with 16px safety margin)
     */
    function initParallax() {
        const parallaxItems = document.querySelectorAll('.agp-image_grid__item--parallax');
        
        if (parallaxItems.length === 0) return;

        // Check for reduced motion preference
        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (prefersReducedMotion) return;

        // Base parallax speed multiplier
        const baseParallaxSpeed = 0.08;
        // Safety margin to ensure border never shows
        const safetyMargin = 16;
        // Base scale for CSS (used as minimum in dynamic mode)
        const baseScale = 1.15;

        function updateParallax() {
            const viewportHeight = window.innerHeight;
            const viewportWidth = window.innerWidth;

            parallaxItems.forEach(item => {
                const parallaxType = item.dataset.parallax;
                const parallaxMode = item.dataset.parallaxMode || 'clamp';
                
                if (parallaxType === 'none') return;

                const image = item.querySelector('.agp-image_grid__image');
                if (!image) return;

                const rect = item.getBoundingClientRect();
                const elementSize = parallaxType === 'vertical' ? rect.height : rect.width;

                // Calculate how far the element is from viewport center
                const elementCenter = rect.top + rect.height / 2;
                const viewportCenter = viewportHeight / 2;
                const offsetFromCenter = elementCenter - viewportCenter;

                // Calculate max travel distance (from entering viewport to leaving)
                const maxTravelDistance = viewportHeight + rect.height;

                let scale = baseScale;
                let parallaxOffset = 0;

                if (parallaxMode === 'dynamic-zoom') {
                    // MODE 1: Dynamic Zoom
                    // Calculate the required scale to cover the maximum parallax offset
                    const rawOffset = offsetFromCenter * baseParallaxSpeed;
                    const maxPossibleOffset = (maxTravelDistance / 2) * baseParallaxSpeed;
                    
                    // Required extra size = maxOffset * 2 (for both directions) + safety margin
                    const requiredExtraSize = (maxPossibleOffset * 2) + safetyMargin * 2;
                    const requiredScale = 1 + (requiredExtraSize / elementSize);
                    
                    // Use the larger of base scale or required scale
                    scale = Math.max(baseScale, requiredScale);
                    parallaxOffset = rawOffset;

                } else if (parallaxMode === 'adaptive-length') {
                    // MODE 2: Adaptive Length
                    // Calculate available margin from current scale and adjust parallax speed accordingly
                    const availableMargin = (elementSize * (baseScale - 1) / 2) - safetyMargin;
                    const maxPossibleOffset = (maxTravelDistance / 2) * baseParallaxSpeed;
                    
                    // Adjust speed so max offset fits within available margin
                    const adaptedSpeed = maxPossibleOffset > 0 
                        ? (availableMargin / (maxTravelDistance / 2)) 
                        : baseParallaxSpeed;
                    
                    parallaxOffset = offsetFromCenter * Math.min(adaptedSpeed, baseParallaxSpeed);
                    scale = baseScale;

                } else {
                    // MODE 3: Clamp (default)
                    // Stop parallax when image border would reach container border
                    const maxOffset = (elementSize * (baseScale - 1) / 2) - safetyMargin;
                    const rawOffset = offsetFromCenter * baseParallaxSpeed;
                    
                    // Clamp to safe bounds
                    parallaxOffset = Math.max(-maxOffset, Math.min(maxOffset, rawOffset));
                    scale = baseScale;
                }

                // Apply transform
                if (parallaxType === 'vertical') {
                    image.style.transform = `scale(${scale}) translateY(${parallaxOffset}px)`;
                } else if (parallaxType === 'horizontal') {
                    image.style.transform = `scale(${scale}) translateX(${parallaxOffset}px)`;
                }
            });
        }

        // Direct scroll listener - no throttling for instant response
        window.addEventListener('scroll', updateParallax, { passive: true });
        window.addEventListener('resize', updateParallax, { passive: true });
        
        // Initial update
        updateParallax();
    }

    /**
     * USP Slider Custom Element
     * Handles auto-rotation, slide/fade transitions, arrow and dot navigation.
     */
    class AgpUspSlider extends HTMLElement {
        constructor() {
            super();
            this.currentIndex = 0;
            this.autoplayInterval = null;
            this.progressAnimation = null;
        }

        connectedCallback() {
            this.slidesContainer = this.querySelector('.agp-usp-slider__slides');
            this.slides = this.querySelectorAll('.agp-usp-slider__slide');
            this.dots = this.querySelectorAll('.agp-usp-slider__dot');
            this.prevBtn = this.querySelector('.agp-usp-slider__arrow--prev');
            this.nextBtn = this.querySelector('.agp-usp-slider__arrow--next');
            // Progress bar is now outside the slider, query from parent
            this.progressBar = this.parentElement?.querySelector('.agp-usp-slider__progress-bar');
            
            if (this.slides.length <= 1) return;

            this.autoplay = this.dataset.autoplay === 'true';
            this.speed = parseInt(this.dataset.speed, 10) || 3000;
            this.transition = this.dataset.transition || 'slide';

            this.setupSlides();
            this.bindEvents();
            
            if (this.autoplay) {
                this.startAutoplay();
            }
        }

        disconnectedCallback() {
            this.stopAutoplay();
        }

        setupSlides() {
            const dotColor = this.dots[0]?.style.borderColor || '#000';
            
            if (this.transition === 'fade') {
                // Fade mode: slides are absolutely positioned
                this.slides.forEach((slide, i) => {
                    slide.style.position = i === 0 ? 'relative' : 'absolute';
                    slide.style.opacity = i === 0 ? '1' : '0';
                    slide.style.top = '0';
                    slide.style.left = '0';
                    slide.style.transition = 'opacity 0.4s ease';
                });
            } else {
                // Slide mode: slides in a row
                this.slidesContainer.style.transition = 'transform 0.4s ease';
                this.goToSlide(0, false);
            }
        }

        bindEvents() {
            this.prevBtn?.addEventListener('click', () => {
                this.prev();
                this.resetAutoplay();
            });

            this.nextBtn?.addEventListener('click', () => {
                this.next();
                this.resetAutoplay();
            });

            this.dots.forEach(dot => {
                dot.addEventListener('click', () => {
                    const index = parseInt(dot.dataset.slide, 10);
                    this.goToSlide(index);
                    this.resetAutoplay();
                });
            });

            // Touch swipe support
            let touchStartX = 0;
            let touchStartY = 0;
            let isSwiping = false;

            this.addEventListener('touchstart', (e) => {
                touchStartX = e.touches[0].clientX;
                touchStartY = e.touches[0].clientY;
                isSwiping = false;
            }, { passive: true });

            this.addEventListener('touchmove', (e) => {
                if (!touchStartX) return;
                const dx = e.touches[0].clientX - touchStartX;
                const dy = e.touches[0].clientY - touchStartY;
                // Lock to horizontal swipe if horizontal movement exceeds vertical
                if (!isSwiping && Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 10) {
                    isSwiping = true;
                }
                // Prevent vertical scroll while swiping horizontally
                if (isSwiping) {
                    e.preventDefault();
                }
            }, { passive: false });

            this.addEventListener('touchend', (e) => {
                if (!isSwiping) { touchStartX = 0; return; }
                const dx = e.changedTouches[0].clientX - touchStartX;
                const threshold = 50;
                if (dx < -threshold) {
                    this.next();
                    this.resetAutoplay();
                } else if (dx > threshold) {
                    this.prev();
                    this.resetAutoplay();
                }
                touchStartX = 0;
                isSwiping = false;
            }, { passive: true });

            // Pause on hover
            this.addEventListener('mouseenter', () => this.stopAutoplay());
            this.addEventListener('mouseleave', () => {
                if (this.autoplay) this.startAutoplay();
            });
        }

        goToSlide(index, animate = true) {
            // Wrap around
            if (index < 0) index = this.slides.length - 1;
            if (index >= this.slides.length) index = 0;

            const previousIndex = this.currentIndex;
            this.currentIndex = index;

            if (this.transition === 'fade') {
                this.slides.forEach((slide, i) => {
                    slide.style.opacity = i === index ? '1' : '0';
                    slide.style.position = i === index ? 'relative' : 'absolute';
                });
            } else {
                const offset = -index * 100;
                this.slidesContainer.style.transform = `translateX(${offset}%)`;
            }

            // Update dots
            this.updateDots(previousIndex, index);
        }

        updateDots(previousIndex, currentIndex) {
            const dotsContainer = this.querySelector('.agp-usp-slider__dots');
            const activeColor = dotsContainer ? getComputedStyle(dotsContainer).getPropertyValue('--dot-active').trim() || '#000' : '#000';
            const inactiveColor = dotsContainer ? getComputedStyle(dotsContainer).getPropertyValue('--dot-inactive').trim() || 'transparent' : 'transparent';
            const activeBorder = dotsContainer ? getComputedStyle(dotsContainer).getPropertyValue('--dot-border-active').trim() || activeColor : activeColor;
            const inactiveBorder = dotsContainer ? getComputedStyle(dotsContainer).getPropertyValue('--dot-border-inactive').trim() || activeColor : activeColor;
            
            if (this.dots[previousIndex]) {
                this.dots[previousIndex].style.background = inactiveColor;
                this.dots[previousIndex].style.borderColor = inactiveBorder;
                this.dots[previousIndex].classList.remove('agp-usp-slider__dot--active');
            }
            if (this.dots[currentIndex]) {
                this.dots[currentIndex].style.background = activeColor;
                this.dots[currentIndex].style.borderColor = activeBorder;
                this.dots[currentIndex].classList.add('agp-usp-slider__dot--active');
            }
        }

        next() {
            this.goToSlide(this.currentIndex + 1);
        }

        prev() {
            this.goToSlide(this.currentIndex - 1);
        }

        startAutoplay() {
            this.stopAutoplay();
            this.startProgress();
            this.autoplayInterval = setInterval(() => {
                this.next();
                this.startProgress();
            }, this.speed);
        }

        stopAutoplay() {
            if (this.autoplayInterval) {
                clearInterval(this.autoplayInterval);
                this.autoplayInterval = null;
            }
            this.pauseProgress();
        }

        startProgress() {
            if (!this.progressBar) return;
            // Reset progress
            this.progressBar.style.transition = 'none';
            this.progressBar.style.width = '0%';
            // Force reflow
            this.progressBar.offsetWidth;
            // Animate to 100%
            this.progressBar.style.transition = `width ${this.speed}ms linear`;
            this.progressBar.style.width = '100%';
        }

        pauseProgress() {
            if (!this.progressBar) return;
            // Get current width and freeze it
            const computedWidth = getComputedStyle(this.progressBar).width;
            this.progressBar.style.transition = 'none';
            this.progressBar.style.width = computedWidth;
        }

        resetAutoplay() {
            if (this.autoplay) {
                this.startAutoplay();
            }
        }
    }

    // Register custom element
    if (!customElements.get('agp-usp-slider')) {
        customElements.define('agp-usp-slider', AgpUspSlider);
    }

    /**
     * AGP Accordion Component
     * Handles expand/collapse with animated icon
     */
    class AgpAccordion extends HTMLElement {
        constructor() {
            super();
            this.button = null;
            this.panel = null;
            this.isOpen = false;
        }

        connectedCallback() {
            this.button = this.querySelector('.agp-accordion__title');
            this.panel = this.querySelector('.agp-accordion__panel');

            if (this.button && this.panel) {
                this.button.addEventListener('click', this.toggle.bind(this));

                // Handle open by default
                if (this.hasAttribute('data-open-default')) {
                    this.isOpen = true;
                    const content = this.panel.querySelector('.agp-accordion__content');
                    if (content) {
                        this.panel.style.maxHeight = content.scrollHeight + 'px';
                    }
                }
            }
        }

        disconnectedCallback() {
            if (this.button) {
                this.button.removeEventListener('click', this.toggle.bind(this));
            }
        }

        toggle() {
            this.isOpen = !this.isOpen;
            this.button.setAttribute('aria-expanded', this.isOpen);
            this.panel.setAttribute('aria-hidden', !this.isOpen);
            this.classList.toggle('is-open', this.isOpen);
            
            // Mark as interacted to enable CSS animations
            if (!this.classList.contains('has-interacted')) {
                this.classList.add('has-interacted');
            }

            if (this.isOpen) {
                // Close other accordions if this one has close-others attribute
                // Track which panels above us will collapse (for scroll calculation)
                let closingHeightAbove = 0;
                if (this.hasAttribute('data-close-others')) {
                    const allAccordions = document.querySelectorAll('agp-accordion');
                    allAccordions.forEach(accordion => {
                        if (accordion !== this && accordion.isOpen) {
                            // Check if this accordion is above us in the DOM
                            if (this.compareDocumentPosition(accordion) & Node.DOCUMENT_POSITION_PRECEDING) {
                                const panel = accordion.querySelector('.agp-accordion__panel');
                                if (panel) {
                                    closingHeightAbove += panel.scrollHeight;
                                }
                            }
                            accordion.close();
                        }
                    });
                }
                
                const content = this.panel.querySelector('.agp-accordion__content');
                if (content) {
                    this.panel.style.maxHeight = content.scrollHeight + 'px';
                }
                
                // Scroll to accordion title only if closing accordions above
                // will push it out of view (behind sticky header or above viewport)
                if (this.hasAttribute('data-scroll-on-open') && closingHeightAbove > 0) {
                    let offset = 0;
                    if (this.hasAttribute('data-scroll-subtract-header')) {
                        const probe = document.createElement('div');
                        probe.style.cssText = 'position:absolute;top:0;left:0;height:var(--sticky-area-height,0px);pointer-events:none;visibility:hidden;';
                        document.body.appendChild(probe);
                        offset = probe.offsetHeight;
                        probe.remove();
                    }
                    // Current position minus the height that will collapse above us
                    const futureTop = this.getBoundingClientRect().top - closingHeightAbove;
                    if (futureTop < offset) {
                        const scrollTarget = this.getBoundingClientRect().top + window.scrollY - closingHeightAbove - offset;
                        window.scrollTo({ top: scrollTarget, behavior: 'smooth' });
                    }
                }
            } else {
                this.panel.style.maxHeight = '0';
            }
        }
        
        closeOtherAccordions() {
            const allAccordions = document.querySelectorAll('agp-accordion');
            allAccordions.forEach(accordion => {
                if (accordion !== this && accordion.isOpen) {
                    accordion.close();
                }
            });
        }
        
        close() {
            if (!this.isOpen) return;
            
            this.isOpen = false;
            this.button.setAttribute('aria-expanded', false);
            this.panel.setAttribute('aria-hidden', true);
            this.classList.remove('is-open');
            
            // Mark as interacted for animation
            if (!this.classList.contains('has-interacted')) {
                this.classList.add('has-interacted');
            }
            
            this.panel.style.maxHeight = '0';
        }
    }

    // Register accordion custom element
    if (!customElements.get('agp-accordion')) {
        customElements.define('agp-accordion', AgpAccordion);
    }

    /**
     * AGP Buy Button
     * 
     * A fully customizable add-to-cart button with dynamic checkout support
     * (Shop Pay, Apple Pay, Google Pay) and out-of-stock notification integration.
     * 
     * Attributes:
     * - data-section-id: Section identifier
     * - data-url: Product URL for variant fetching
     * - data-atc-text: Add to cart button text
     * - data-preorder-text: Pre-order button text  
     * - data-sold-out-text: Sold out button text
     * - data-notification-text: Notification button text
     * - data-enable-notification: "true" | "false" - Enable back-in-stock notification
     * - data-is-preorder: "true" | "false" - Whether product is pre-order
     * 
     * Events:
     * - Listens to form's `variant:change` event for variant updates
     * - Notification button triggers Klaviyo back-in-stock modal
     */
    class AgpBuyButton extends HTMLElement {
        constructor() {
            super();
            this.formElement = null;
            this.atcWrapper = null;
            this.atcButton = null;
            this.notificationWrapper = null;
            this.notificationButton = null;
            this.dynamicCheckout = null;
            this.variantIdInput = null;
            this.isLoading = false;
            this.cartData = null;
            this.currentVariant = null;
            this.isMaxInCart = false;
            this.variantInventories = {}; // Inventory data passed from Liquid
            
            // Bound event handlers
            this.onVariantChange = this.handleVariantChange.bind(this);
            this.onNotificationClick = this.handleNotificationClick.bind(this);
            this.onLoadingStart = this.startLoading.bind(this);
            this.onLoadingEnd = this.handleLoadingEnd.bind(this);
            this.onCartChange = this.handleCartChange.bind(this);
        }

        connectedCallback() {
            // Get form element from attribute
            const formId = this.getAttribute('form');
            this.formElement = formId ? document.getElementById(formId) : this.querySelector('form');
            
            // Cache DOM elements
            this.atcWrapper = this.querySelector('[data-atc-wrapper]');
            this.atcButton = this.querySelector('[data-atc-button]');
            this.notificationWrapper = this.querySelector('[data-notification-wrapper]');
            this.notificationButton = this.querySelector('[data-notification-button]');
            this.dynamicCheckout = this.querySelector('[data-dynamic-checkout]');
            this.variantIdInput = this.querySelector('[data-variant-id]');

            // Match the wallet button height to the Add-to-cart button. The wallet renders in a
            // closed shadow DOM, so the only lever is Shopify's supported block-size custom
            // property (clamped to its 25–55px range); the ATC's height comes from its padding,
            // so measure it and keep it in sync while it changes (font load, size setting, resize).
            if (this.dynamicCheckout && this.atcButton) {
                const syncWalletHeight = () => {
                    const h = this.atcButton.getBoundingClientRect().height;
                    if (!h) return;
                    const clamped = Math.max(25, Math.min(55, Math.round(h)));
                    this.dynamicCheckout.style.setProperty('--shopify-accelerated-checkout-button-block-size', clamped + 'px');
                };
                const syncRow = () => { syncWalletHeight(); this._fitSplit(); };
                syncRow();
                if ('ResizeObserver' in window) {
                    this._walletHeightRo = new ResizeObserver(syncRow);
                    this._walletHeightRo.observe(this.atcButton);
                    this._walletHeightRo.observe(this);
                }
                // The wallet renders (and re-renders on variant change) after us, and its label is
                // localized — so the truncation guard has to re-measure whenever it changes.
                this._walletMo = new MutationObserver(syncRow);
                this._walletMo.observe(this.dynamicCheckout, { childList: true, subtree: true });
                // Crossing 1000px changes which layout / split is live.
                this._expressMq = window.matchMedia('(min-width: 1000px)');
                this._onExpressMq = syncRow;
                this._expressMq.addEventListener('change', this._onExpressMq);
            }

            // The section's per-wallet "Hide Shop Pay / Apple Pay / …" switches: hide the
            // dynamic-checkout block when Shopify serves a wallet the merchant turned off.
            // Uses a class (not inline display) because the availability logic below keeps
            // rewriting dynamicCheckout.style.display.
            if (this.dynamicCheckout && window.agpWatchWallet && window.agpAnyWalletHidden()) {
                const host = this;
                window.agpWatchWallet(this.dynamicCheckout, function (name) {
                    host.classList.toggle('agp-buy-button--wallet-hidden', window.agpWalletHidden(name));
                    // Add to cart just went full width (or back to the split): re-measure, since
                    // the class lives on the host and the wallet observer never sees it change.
                    if (host._fitSplit) host._fitSplit();
                });
            }

            // Listen to variant changes from the main product form
            if (this.formElement) {
                this.formElement.addEventListener('variant:change', this.onVariantChange);
            }
            
            // Add notification button click handler
            if (this.notificationButton) {
                this.notificationButton.addEventListener('click', this.onNotificationClick);
            }
            
            // Listen for theme loading events (dispatched by product-form)
            document.documentElement.addEventListener('theme:loading:start', this.onLoadingStart);
            document.documentElement.addEventListener('theme:loading:end', this.onLoadingEnd);
            
            // Listen for cart changes (theme dispatches cart:change on documentElement which bubbles to document;
            // our own AgpStickyAtc dispatches cart:refresh and cart:add on document)
            document.addEventListener('cart:change', this.onCartChange);
            document.addEventListener('cart:refresh', this.onCartChange);
            document.addEventListener('cart:add', this.onCartChange);
            
            // Also listen on form for variant:add (success) and cart:error
            if (this.formElement) {
                this.formElement.addEventListener('variant:add', this.onLoadingEnd);
                this.formElement.addEventListener('cart:error', this.onLoadingEnd);
            }
            
            // Parse variant inventory data from Liquid (guaranteed to have inventory info)
            try {
                const inventoryData = this.dataset.variantInventories;
                if (inventoryData) {
                    this.variantInventories = JSON.parse(inventoryData);
                }
            } catch (e) {
                console.error('AGP Buy Button: Error parsing variant inventories', e);
            }
            
            // Initialize current variant from data attribute
            this.initializeCurrentVariant();
            
            // Fetch initial cart data and check inventory
            this.fetchCartAndCheckInventory();
        }
        
        /**
         * Truncation guard for the side-by-side row — see window.agpFitSplitRow at the top of this
         * file for the mechanics. Inert unless the active breakpoint is side-by-side and the
         * dynamic-checkout button is actually shown.
         */
        _fitSplit() {
            if (!window.agpFitSplitRow) return;
            const sideBySide = window.matchMedia('(min-width: 1000px)').matches
                ? this.classList.contains('agp-buy-button--sbs-desktop')
                : this.classList.contains('agp-buy-button--sbs-mobile');
            window.agpFitSplitRow({
                host: this,
                row: this.formElement,
                atc: this.atcButton,
                slot: this.dynamicCheckout,
                minClass: 'agp-buy-button--express-min',
                stackClass: 'agp-buy-button--stacked-fallback',
                active: sideBySide && !this.classList.contains('agp-buy-button--wallet-hidden'),
                autoWidth: this.dataset.autoWidth === '1',
                allowStacked: this.dataset.allowStacked === '1',
            });
        }

        /**
         * Initialize current variant from data attribute
         * Uses Liquid-provided inventory data, no need to fetch product.js
         */
        initializeCurrentVariant() {
            const initialVariantId = parseInt(this.dataset.currentVariantId);
            if (!initialVariantId) return;
            
            // Get inventory info from Liquid-provided data
            const inventoryInfo = this.variantInventories[String(initialVariantId)];
            
            // Create a minimal variant object with the data we need
            this.currentVariant = {
                id: initialVariantId,
                inventory_quantity: inventoryInfo?.inventory_quantity ?? 0,
                inventory_policy: inventoryInfo?.inventory_policy ?? 'deny',
                available: inventoryInfo?.available ?? true
            };
        }
        
        /**
         * Get variant with inventory data
         * Uses Liquid-provided inventory data as primary source (always available)
         * @param {Object} variant - The variant object
         * @returns {Object} Variant with inventory data
         */
        getVariantWithInventory(variant) {
            if (!variant) return null;
            
            // Primary source: Liquid-provided inventory data (always has inventory info)
            const inventoryInfo = this.variantInventories[String(variant.id)];
            if (inventoryInfo) {
                return {
                    ...variant,
                    inventory_quantity: inventoryInfo.inventory_quantity,
                    inventory_policy: inventoryInfo.inventory_policy,
                    available: inventoryInfo.available
                };
            }
            
            // Fallback: If variant already has inventory data, use it
            if (typeof variant.inventory_quantity === 'number') {
                return variant;
            }
            
            // Fallback: Look up in cached product data from .js endpoint
            if (this.productData?.variants) {
                const fullVariant = this.productData.variants.find(v => v.id === variant.id);
                if (fullVariant && typeof fullVariant.inventory_quantity === 'number') {
                    return fullVariant;
                }
            }
            
            return variant;
        }

        disconnectedCallback() {
            if (this.formElement) {
                this.formElement.removeEventListener('variant:change', this.onVariantChange);
                this.formElement.removeEventListener('variant:add', this.onLoadingEnd);
                this.formElement.removeEventListener('cart:error', this.onLoadingEnd);
            }
            if (this.notificationButton) {
                this.notificationButton.removeEventListener('click', this.onNotificationClick);
            }
            if (this._walletHeightRo) { this._walletHeightRo.disconnect(); this._walletHeightRo = null; }
            if (this._walletMo) { this._walletMo.disconnect(); this._walletMo = null; }
            if (this._expressMq && this._onExpressMq) { this._expressMq.removeEventListener('change', this._onExpressMq); }
            document.documentElement.removeEventListener('theme:loading:start', this.onLoadingStart);
            document.documentElement.removeEventListener('theme:loading:end', this.onLoadingEnd);
            document.removeEventListener('cart:change', this.onCartChange);
            document.removeEventListener('cart:refresh', this.onCartChange);
            document.removeEventListener('cart:add', this.onCartChange);
        }
        
        /**
         * Fetch cart data and check inventory levels
         */
        async fetchCartAndCheckInventory() {
            try {
                const response = await fetch(`${Shopify.routes.root}cart.js`);
                this.cartData = await response.json();
                this.checkMaxInCart();
            } catch (error) {
                console.error('AGP Buy Button: Error fetching cart', error);
            }
        }
        
        /**
         * Handle cart change events
         */
        handleCartChange(event) {
            // Update cart data from event if available, otherwise refetch
            if (event.detail?.cart) {
                this.cartData = event.detail.cart;
                this.checkMaxInCart();
            } else {
                this.fetchCartAndCheckInventory();
            }
        }
        
        /**
         * Check if current variant has reached max quantity in cart
         */
        checkMaxInCart() {
            if (!this.cartData || !this.currentVariant) return;
            
            // Get variant with full inventory data
            const variant = this.getVariantWithInventory(this.currentVariant);
            if (!variant) return;
            
            const variantId = variant.id;
            const variantInventory = variant.inventory_quantity;
            const inventoryPolicy = variant.inventory_policy;
            
            // If inventory policy is 'continue', they can always add more
            if (inventoryPolicy === 'continue') {
                this.setMaxInCartState(false);
                return;
            }
            
            // If inventory quantity is not available, we can't check
            if (typeof variantInventory !== 'number') {
                return;
            }
            
            // Find this variant in cart
            const cartItem = this.cartData.items?.find(item => item.variant_id === variantId);
            const quantityInCart = cartItem?.quantity || 0;
            
            // Check if quantity in cart >= available inventory
            const isMaxReached = quantityInCart >= variantInventory && variantInventory > 0;
            
            this.setMaxInCartState(isMaxReached);
        }
        
        /**
         * Set max in cart state and update button
         * @param {boolean} isMax - Whether max quantity is reached
         */
        setMaxInCartState(isMax) {
            if (this.isMaxInCart === isMax) return;
            
            this.isMaxInCart = isMax;
            
            if (!this.atcButton) return;
            
            if (isMax) {
                // Disable button and update text
                this.atcButton.disabled = true;
                const textElement = this.atcButton.querySelector('[data-button-text]');
                if (textElement) {
                    textElement.textContent = this.dataset.maxInCartText || 'Max quantity reached';
                }
                
                // Apply custom disabled styling if provided
                this.applyDisabledStyling();
                
                // Hide dynamic checkout
                if (this.dynamicCheckout) {
                    this.dynamicCheckout.style.display = 'none';
                }
            } else {
                // Re-enable button if variant is available
                if (this.currentVariant?.available) {
                    this.atcButton.disabled = false;
                    const isPreorder = this.dataset.isPreorder === 'true';
                    const textElement = this.atcButton.querySelector('[data-button-text]');
                    if (textElement) {
                        textElement.textContent = isPreorder 
                            ? (this.dataset.preorderText || 'Pre-order')
                            : (this.dataset.atcText || 'Add to cart');
                    }
                }
                
                // Remove custom disabled styling
                this.removeDisabledStyling();
                
                // Show dynamic checkout again, unless the variant is out of stock and the
                // "hide when out of stock" setting is on. currentVariant is null before the first
                // variant:change, so fall back to what Liquid rendered rather than assuming stock.
                if (this.dynamicCheckout) {
                    const hideWhenUnavailable = this.dataset.dcHideUnavailable !== '0';
                    const avail = this.currentVariant
                        ? this.currentVariant.available
                        : this.dataset.variantAvailable !== 'false';
                    if (avail || !hideWhenUnavailable) this.dynamicCheckout.style.display = '';
                }
            }
        }
        
        /**
         * Apply custom disabled button styling
         */
        applyDisabledStyling() {
            if (!this.atcButton) return;
            
            const disabledBg = this.dataset.disabledBg;
            const disabledText = this.dataset.disabledText;
            
            if (disabledBg) {
                const rgbBg = this.hexToRgb(disabledBg);
                if (rgbBg) {
                    this.atcButton.style.setProperty('--agp-btn-disabled-bg', rgbBg);
                }
            }
            if (disabledText) {
                const rgbText = this.hexToRgb(disabledText);
                if (rgbText) {
                    this.atcButton.style.setProperty('--agp-btn-disabled-text', rgbText);
                }
            }
            
            this.atcButton.classList.add('is-max-in-cart');
            this.classList.add('is-max-in-cart');
        }
        
        /**
         * Convert hex color to RGB values string
         * @param {string} hex - Hex color (e.g., "#ED8A00" or "ED8A00")
         * @returns {string|null} RGB values string (e.g., "237, 138, 0") or null if invalid
         */
        hexToRgb(hex) {
            if (!hex) return null;
            
            // Remove # if present
            hex = hex.replace(/^#/, '');
            
            // Handle shorthand (e.g., "F00" -> "FF0000")
            if (hex.length === 3) {
                hex = hex.split('').map(c => c + c).join('');
            }
            
            if (hex.length !== 6) return null;
            
            const r = parseInt(hex.substring(0, 2), 16);
            const g = parseInt(hex.substring(2, 4), 16);
            const b = parseInt(hex.substring(4, 6), 16);
            
            if (isNaN(r) || isNaN(g) || isNaN(b)) return null;
            
            return `${r}, ${g}, ${b}`;
        }
        
        /**
         * Remove custom disabled button styling
         */
        removeDisabledStyling() {
            if (!this.atcButton) return;
            
            // Only remove if we applied custom styling
            if (this.dataset.disabledBg) {
                this.atcButton.style.removeProperty('--agp-btn-disabled-bg');
            }
            if (this.dataset.disabledText) {
                this.atcButton.style.removeProperty('--agp-btn-disabled-text');
            }
            
            this.atcButton.classList.remove('is-max-in-cart');
            this.classList.remove('is-max-in-cart');
        }
        
        /**
         * Handle loading end and recheck cart
         */
        handleLoadingEnd() {
            this.stopLoading();
            // Refetch cart after adding to cart
            setTimeout(() => this.fetchCartAndCheckInventory(), 100);
        }
        
        /**
         * Start loading state
         */
        startLoading() {
            if (this.isLoading) return;
            // Only show loading if this form's button was clicked (check aria-busy)
            if (!this.atcButton?.getAttribute('aria-busy')) return;
            
            this.isLoading = true;
            this.classList.add('is-loading');
        }
        
        /**
         * Stop loading state
         */
        stopLoading() {
            if (!this.isLoading) return;
            this.isLoading = false;
            this.classList.remove('is-loading');
        }

        /**
         * Handle variant change events from the product form
         * @param {CustomEvent} event - The variant:change event
         */
        handleVariantChange(event) {
            const variant = event.detail?.variant;
            const enableNotification = this.dataset.enableNotification === 'true';
            const isPreorder = this.dataset.isPreorder === 'true';
            const showPrice = this.dataset.showPrice === 'true';
            
            // Store current variant for inventory checks
            // Use the variant with full inventory data if available
            this.currentVariant = variant ? this.getVariantWithInventory(variant) : null;
            
            // Reset max in cart state when variant changes
            this.isMaxInCart = false;
            this.removeDisabledStyling();
            
            // Update variant ID in the hidden input
            if (this.variantIdInput && variant) {
                this.variantIdInput.value = variant.id;
            }
            
            // Format price if needed
            let priceText = '';
            if (showPrice && variant) {
                priceText = this.formatMoney(variant.price);
            }
            
            if (!variant) {
                // Variant unavailable (no matching variant for selected options)
                this.updateButtonState({
                    isAvailable: false,
                    buttonText: window.themeVariables?.strings?.unavailableButton || 'Unavailable',
                    priceText: '',
                    enableNotification: false // Don't show notification for unavailable variants
                });
            } else if (variant.available) {
                // Variant is in stock
                const text = isPreorder 
                    ? (this.dataset.preorderText || window.themeVariables?.strings?.preOrderButton || 'Pre-order')
                    : (this.dataset.atcText || window.themeVariables?.strings?.addToCartButton || 'Add to cart');
                    
                this.updateButtonState({
                    isAvailable: true,
                    buttonText: text,
                    priceText: priceText,
                    enableNotification: false
                });
                
                // Check max in cart after updating state
                this.checkMaxInCart();
            } else {
                // Variant is out of stock
                if (enableNotification) {
                    this.updateButtonState({
                        isAvailable: false,
                        buttonText: this.dataset.notificationText || 'Notify me when available',
                        priceText: '',
                        enableNotification: true
                    });
                } else {
                    this.updateButtonState({
                        isAvailable: false,
                        buttonText: this.dataset.soldOutText || window.themeVariables?.strings?.soldOutButton || 'Sold out',
                        priceText: priceText,
                        enableNotification: false
                    });
                }
            }
        }
        
        /**
         * Format money value (cents to formatted currency)
         * @param {number} cents - Price in cents
         * @returns {string} Formatted price
         */
        formatMoney(cents) {
            const formatter = new Intl.NumberFormat(Shopify.locale || 'en', {
                style: 'currency',
                currency: Shopify.currency?.active || 'EUR'
            });
            let formatted = formatter.format(cents / 100);
            // Append currency code if theme setting enables it (matches Shopify's money_with_currency filter)
            if (this.dataset.currencyCodeEnabled === 'true') {
                const code = Shopify.currency?.active || 'EUR';
                if (!formatted.includes(code)) {
                    formatted += ' ' + code;
                }
            }
            return formatted;
        }

        /**
         * Update the button state based on variant availability
         * @param {Object} state - The state object
         * @param {boolean} state.isAvailable - Whether the variant is available
         * @param {string} state.buttonText - The button text to display
         * @param {string} state.priceText - The price text to display
         * @param {boolean} state.enableNotification - Whether to show notification button
         */
        updateButtonState({ isAvailable, buttonText, priceText, enableNotification }) {
            // Determine what to show:
            // - If available: show ATC, hide notification
            // - If not available + notification enabled: hide ATC, show notification
            // - If not available + notification disabled: show ATC (disabled with sold out text), hide notification
            const showAtc = isAvailable || !enableNotification;
            const showNotification = !isAvailable && enableNotification;
            
            // Update ATC button visibility and state
            if (this.atcWrapper) {
                this.atcWrapper.style.display = showAtc ? '' : 'none';
            }
            
            if (this.atcButton) {
                this.atcButton.disabled = !isAvailable;
                // Update button text using data-button-text element
                const textElement = this.atcButton.querySelector('[data-button-text]');
                if (textElement) {
                    textElement.textContent = buttonText;
                }
                // Update price using data-button-price element
                const priceElement = this.atcButton.querySelector('[data-button-price]');
                if (priceElement && priceText) {
                    priceElement.textContent = priceText;
                }
            }
            
            // Update notification button visibility
            if (this.notificationWrapper) {
                this.notificationWrapper.style.display = showNotification ? '' : 'none';
            }
            
            // Update dynamic checkout visibility. "Hide when out of stock" is a setting (default
            // on) — with it off, the wallet stays visible on a sold-out variant.
            if (this.dynamicCheckout) {
                const hideWhenUnavailable = this.dataset.dcHideUnavailable !== '0';
                this.dynamicCheckout.style.display = (isAvailable || !hideWhenUnavailable) ? '' : 'none';
            }
        }

        /**
         * Handle notification button click — redirect to product page.
         * 
         * When inside a quick-buy modal (collection page / product slider),
         * navigates to the product page with the selected variant pre-selected.
         * On the product page itself, Klaviyo's BIS handles the click natively.
         */
        handleNotificationClick() {
            const variantId = this.variantIdInput?.value;
            const productUrl = this.dataset.url;

            // Only redirect when inside a quick-buy modal context
            // (i.e. we're NOT already on the product page)
            const isQuickBuy = !!this.closest('quick-buy-modal, .quick-buy-modal');
            if (isQuickBuy && productUrl) {
                window.location.href = variantId ? `${productUrl}?variant=${variantId}` : productUrl;
            }
        }
    }

    // Register buy button custom element
    if (!customElements.get('agp-buy-button')) {
        customElements.define('agp-buy-button', AgpBuyButton);
    }

    /**
     * AGP Sticky Add to Cart
     * 
     * A sticky add to cart bar that appears when the main ATC button scrolls
     * out of view and hides when the footer comes into view.
     * 
     * Attributes:
     * - data-animation: "fade" | "slide" - Animation type
     * - data-animation-duration: Transition duration in ms
     * - data-enable-back-in-stock: "true" | "false" - Enable back in stock popup
     * - data-form-id: ID of the product form to submit to
     * - data-is-preorder: "true" | "false" - Is this a pre-order product
     * - data-variant-inventories: JSON with inventory data per variant
     */
    class AgpStickyAtc extends HTMLElement {
        constructor() {
            super();
            this.isVisible = false;
            this.isLoading = false;
            this.formElement = null;
            this.latestFormCondition = false;
            this.latestScrollCondition = true;
            this.variants = [];
            this.currentVariant = null;
            this.variantInventories = {};
            this.variantPrices = {};
            this.isPreorder = false;
            this.cartQuantities = {};
            this.notificationClasses = [];
            this.variantSelects = [];
            this.buttons = [];
            
            // Bind methods
            this.onIntersection = this.onIntersection.bind(this);
            this.onVariantChange = this.onVariantChange.bind(this);
            this.onButtonClick = this.onButtonClick.bind(this);
            this.onMainFormVariantChange = this.onMainFormVariantChange.bind(this);
            this.onCartUpdate = this.onCartUpdate.bind(this);
            this.onScroll = this.onScroll.bind(this);
        }
        
        connectedCallback() {
            // Move to body to escape any parent stacking contexts
            // This ensures the fixed positioning works relative to viewport
            if (this.parentElement !== document.body) {
                document.body.appendChild(this);
                return; // connectedCallback will be called again once appended to body
            }
            
            // Parse preorder and inventory data
            this.isPreorder = this.dataset.isPreorder === 'true';
            if (this.dataset.variantInventories) {
                try {
                    this.variantInventories = JSON.parse(this.dataset.variantInventories);
                } catch (e) {
                    console.error('AGP Sticky ATC: Failed to parse variant inventories', e);
                }
            }
            
            // Parse Liquid-rendered variant prices (locale-correct, no Intl.NumberFormat needed)
            if (this.dataset.variantPrices) {
                try {
                    this.variantPrices = JSON.parse(this.dataset.variantPrices);
                } catch (e) {
                    console.error('AGP Sticky ATC: Failed to parse variant prices', e);
                }
            }
            
            // Parse notification custom classes
            if (this.dataset.notificationClasses) {
                this.notificationClasses = this.dataset.notificationClasses.split(' ').filter(cls => cls.trim());
            }
            
            // Get DOM elements
            this.formId = this.dataset.formId;
            this.formElement = document.getElementById(this.formId);
            // Support multiple buttons (one per layout - full and compact)
            this.buttons = this.querySelectorAll('[data-sticky-atc-button]');
            // Support multiple variant selects (one per layout) with agp-select wrapper
            // The data-sticky-variant-select attribute is on the select element itself
            this.variantSelects = [];
            this.querySelectorAll('[data-sticky-variant-select]').forEach(el => {
                // Check if the element itself is a select or if it's a wrapper containing one
                if (el.tagName === 'SELECT') {
                    this.variantSelects.push(el);
                } else {
                    const select = el.querySelector('select, .agp-select__native');
                    if (select) this.variantSelects.push(select);
                }
            });
            this.priceElements = this.querySelectorAll('[data-sticky-price]');
            this.buttonTextElements = this.querySelectorAll('[data-button-text]');
            this.imageElement = this.querySelector('[data-sticky-image] img');
            this.variantNameElement = this.querySelector('[data-sticky-variant-name]');
            this.isFullLayout = this.dataset.layout === 'full';
            
            // Parse variants JSON
            const variantsJson = document.querySelector('[data-sticky-atc-variants]');
            if (variantsJson) {
                try {
                    this.variants = JSON.parse(variantsJson.textContent);
                    // Set initial variant from first select or from main form
                    const firstSelect = this.variantSelects[0];
                    const selectedOption = firstSelect?.querySelector('option:checked');
                    if (selectedOption) {
                        this.currentVariant = this.variants.find(v => v.id === parseInt(selectedOption.value));
                    } else {
                        // For full layout or when no select, get from main form or URL
                        const mainVariantInput = this.formElement?.querySelector('input[name="id"]');
                        if (mainVariantInput) {
                            this.currentVariant = this.variants.find(v => v.id === parseInt(mainVariantInput.value));
                        } else if (this.variants.length > 0) {
                            // Fallback to first available or first variant
                            this.currentVariant = this.variants.find(v => v.available) || this.variants[0];
                        }
                    }
                } catch (e) {
                    console.error('AGP Sticky ATC: Failed to parse variants', e);
                }
            }
            
            // Check for debug mode (always visible)
            this.isDebugMode = this.dataset.debug === 'true';
            
            if (this.isDebugMode) {
                // In debug mode, always show the sticky bar
                this.isVisible = true;
                this.classList.add('is-visible');
            } else {
                // Setup IntersectionObserver for normal behavior
                this.observer = new IntersectionObserver(this.onIntersection, {
                    threshold: 0
                });
                
                // Find the buy buttons element to observe
                const buyButtons = this.formElement?.querySelector('buy-buttons, .buy-buttons, [data-buy-buttons]');
                if (buyButtons) {
                    this.buyButtonsElement = buyButtons;
                    this.observer.observe(buyButtons);
                } else if (this.formElement) {
                    // Fallback to form element
                    this.buyButtonsElement = this.formElement;
                    this.observer.observe(this.formElement);
                }
                
                // Observe footer
                window.addEventListener('scroll', this.onScroll, { passive: true });
            }
            
            // Event listeners for all variant selects
            this.variantSelects.forEach(select => {
                select.addEventListener('change', this.onVariantChange);
            });
            // Event listeners for all buttons
            this.buttons.forEach(button => {
                button.addEventListener('click', this.onButtonClick);
            });
            
            // Listen to main form variant changes on document (variant:change bubbles).
            // We listen on document instead of formElement because block rendering
            // order may cause formElement to not exist yet at connectedCallback time.
            document.addEventListener('variant:change', this.onMainFormVariantChange);
            
            // Poll the hidden variant input as fallback for themes that don't dispatch events.
            // Defer lookup if formElement isn't available yet (block ordering).
            this.setupVariantInputPoll();
            
            // Listen for cart updates to check max-in-cart
            // (cart:refresh/cart:add from our own components, cart:change from theme on item removal/quantity change)
            document.addEventListener('cart:refresh', this.onCartUpdate);
            document.addEventListener('cart:add', this.onCartUpdate);
            document.addEventListener('cart:change', this.onCartUpdate);
            
            // Fetch initial cart quantities
            this.fetchCartQuantities();
        }
        
        disconnectedCallback() {
            this.observer?.disconnect();
            window.removeEventListener('scroll', this.onScroll);
            if (this.variantInputPoll) {
                clearInterval(this.variantInputPoll);
            }
            this.variantSelects.forEach(select => {
                select.removeEventListener('change', this.onVariantChange);
            });
            this.buttons.forEach(button => {
                button.removeEventListener('click', this.onButtonClick);
            });
            this.formElement?.removeEventListener('variant:change', this.onMainFormVariantChange);
            document.removeEventListener('variant:change', this.onMainFormVariantChange);
            document.removeEventListener('cart:refresh', this.onCartUpdate);
            document.removeEventListener('cart:add', this.onCartUpdate);
            document.removeEventListener('cart:change', this.onCartUpdate);
        }
        
        async fetchCartQuantities() {
            try {
                const response = await fetch('/cart.js');
                const cart = await response.json();
                this.cartQuantities = {};
                cart.items.forEach(item => {
                    this.cartQuantities[item.variant_id] = (this.cartQuantities[item.variant_id] || 0) + item.quantity;
                });
                this.updateUI();
            } catch (e) {
                console.error('AGP Sticky ATC: Failed to fetch cart', e);
            }
        }
        
        onCartUpdate() {
            this.fetchCartQuantities();
        }
        
        /**
         * Set up variant input polling with deferred form lookup.
         * If the form element doesn't exist yet (block ordering), retries after DOM is ready.
         */
        setupVariantInputPoll() {
            const trySetup = () => {
                if (!this.formElement) {
                    this.formElement = document.getElementById(this.formId);
                }
                if (!this.formElement) return false;
                
                // Set up observer if not done yet (form wasn't available at connectedCallback time)
                if (!this.buyButtonsElement && !this.isDebugMode) {
                    const buyButtons = this.formElement.querySelector('buy-buttons, .buy-buttons, [data-buy-buttons]');
                    if (buyButtons) {
                        this.buyButtonsElement = buyButtons;
                        this.observer?.observe(buyButtons);
                    } else {
                        this.buyButtonsElement = this.formElement;
                        this.observer?.observe(this.formElement);
                    }
                }
                
                // Set up initial variant from form if not yet determined
                if (!this.currentVariant || this.currentVariant === this.variants[0]) {
                    const mainVariantInput = this.formElement.querySelector('input[name="id"]');
                    if (mainVariantInput) {
                        const formVariant = this.variants.find(v => v.id === parseInt(mainVariantInput.value));
                        if (formVariant) this.currentVariant = formVariant;
                    }
                }
                
                // Set up polling
                this.variantInput = this.formElement.querySelector('input[name="id"]');
                if (this.variantInput) {
                    this.lastVariantInputValue = this.variantInput.value;
                    this.variantInputPoll = setInterval(() => {
                        if (this.variantInput.value !== this.lastVariantInputValue) {
                            this.lastVariantInputValue = this.variantInput.value;
                            const newVariantId = parseInt(this.variantInput.value);
                            if (this.currentVariant?.id !== newVariantId) {
                                this.currentVariant = this.variants.find(v => v.id === newVariantId);
                                this.variantSelects.forEach(select => {
                                    if (select.value !== String(newVariantId)) {
                                        select.value = newVariantId;
                                    }
                                });
                                this.updateUI();
                            }
                        }
                    }, 100);
                }
                return true;
            };
            
            // Try immediately, then defer if form not found
            if (!trySetup()) {
                requestAnimationFrame(() => {
                    if (!trySetup()) {
                        // Last resort: try after DOM is fully loaded
                        if (document.readyState === 'loading') {
                            document.addEventListener('DOMContentLoaded', () => trySetup(), { once: true });
                        } else {
                            setTimeout(() => trySetup(), 100);
                        }
                    }
                });
            }
        }
        
        onIntersection(entries) {
            entries.forEach(entry => {
                if (entry.target === this.buyButtonsElement || entry.target === this.formElement) {
                    if (!entry.isIntersecting) {
                        const isAboveViewport = entry.boundingClientRect.bottom < 0;
                        const isBelowViewport = entry.boundingClientRect.top > window.innerHeight;
                        
                        if (isAboveViewport) {
                            // Button scrolled past (normal behavior)
                            this.latestFormCondition = true;
                        } else if (isBelowViewport) {
                            // Button is below viewport — check per-device setting
                            const isMobile = window.innerWidth < 1000;
                            const showAbove = isMobile 
                                ? this.dataset.showAboveButtonMobile === 'true'
                                : this.dataset.showAboveButtonDesktop === 'true';
                            this.latestFormCondition = showAbove;
                        } else {
                            this.latestFormCondition = false;
                        }
                    } else {
                        this.latestFormCondition = false;
                    }
                }
            });
            
            this.updateVisibility();
        }
        
        onScroll() {
            // Hide when within the last 200px of the page
            const distanceFromBottom = document.documentElement.scrollHeight - window.scrollY - window.innerHeight;
            const newCondition = distanceFromBottom > 200;
            if (newCondition !== this.latestScrollCondition) {
                this.latestScrollCondition = newCondition;
                this.updateVisibility();
            }
        }
        
        updateVisibility() {
            const shouldShow = this.latestFormCondition && this.latestScrollCondition;
            
            if (shouldShow !== this.isVisible) {
                this.isVisible = shouldShow;
                this.classList.toggle('is-visible', shouldShow);
            }
        }
        
        onVariantChange(event) {
            const selectedId = parseInt(event.target.value);
            this.currentVariant = this.variants.find(v => v.id === selectedId);
            
            // Sync all sticky ATC variant selects
            this.variantSelects.forEach(select => {
                if (select !== event.target && select.value !== String(selectedId)) {
                    select.value = selectedId;
                }
            });
            
            // Try to use the theme's variant-picker element's selectVariant method
            // Find the variant-picker linked to our form
            let variantPicker = document.querySelector(`variant-picker[form="${this.formId}"]`);
            
            // If not found with form attribute, try to find any variant-picker on the page
            if (!variantPicker) {
                variantPicker = document.querySelector('variant-picker');
            }
            
            if (variantPicker && typeof variantPicker.selectVariant === 'function') {
                variantPicker.selectVariant(selectedId);
            } else {
                // Fallback: Update the master selector (input[name="id"]) directly
                const mainVariantInput = this.formElement?.querySelector('input[name="id"]');
                if (mainVariantInput && mainVariantInput.value !== String(selectedId)) {
                    mainVariantInput.value = selectedId;
                    mainVariantInput.dispatchEvent(new Event('change', { bubbles: true }));
                }
                
                // Also dispatch variant:change on the form for other components to listen
                this.formElement?.dispatchEvent(new CustomEvent('variant:change', {
                    bubbles: true,
                    detail: {
                        variant: this.currentVariant
                    }
                }));
            }
            
            // Update URL
            const newUrl = new URL(window.location.href);
            newUrl.searchParams.set('variant', selectedId);
            history.replaceState({}, '', newUrl.toString());
            
            this.updateUI();
        }
        
        onMainFormVariantChange(event) {
            // When main form variant changes (from theme's variant picker), sync sticky bar.
            // Filter by form ID since we listen on document (variant:change bubbles).
            const sourceForm = event.target.closest('form') || event.target;
            if (this.formId && sourceForm.id !== this.formId) {
                return;
            }
            
            const newVariant = event.detail?.variant;
            if (newVariant) {
                const newVariantId = newVariant.id;
                // Sync selects if they exist
                this.variantSelects.forEach(select => {
                    if (select.value !== String(newVariantId)) {
                        select.value = newVariantId;
                    }
                });
                this.currentVariant = this.variants.find(v => v.id === newVariantId);
                this.updateUI();
            }
        }
        
        updateUI() {
            if (!this.currentVariant) return;
            
            const variantId = this.currentVariant.id;
            const isAvailable = this.currentVariant.available;
            const enableBackInStock = this.dataset.enableBackInStock === 'true';
            
            // Check max-in-cart state (use String key for JSON lookup)
            const inventoryData = this.variantInventories[String(variantId)];
            const cartQty = this.cartQuantities[variantId] || 0;
            let isMaxInCart = false;
            
            if (inventoryData && isAvailable) {
                // Only check max if inventory policy is 'deny' (don't allow overselling)
                if (inventoryData.inventory_policy === 'deny') {
                    const maxQty = inventoryData.inventory_quantity;
                    if (cartQty >= maxQty && maxQty > 0) {
                        isMaxInCart = true;
                    }
                }
            }
            
            // Get formatted price — prefer Liquid-rendered prices (locale-correct)
            // over Intl.NumberFormat which varies by browser locale
            let formattedPrice = null;
            const variantPriceFromMap = this.variantPrices?.[String(this.currentVariant.id)];
            if (variantPriceFromMap) {
                formattedPrice = variantPriceFromMap;
            } else {
                // Fallback: try select option data-price attribute
                const firstSelect = this.variantSelects[0];
                if (firstSelect) {
                    const selectedOption = firstSelect.querySelector(`option[value="${this.currentVariant.id}"]`);
                    if (selectedOption) {
                        formattedPrice = selectedOption.dataset.price;
                    }
                }
            }
            
            // Update all price elements
            this.priceElements.forEach(el => {
                el.textContent = formattedPrice || this.formatMoney(this.currentVariant.price);
            });
            
            // Update variant name (full layout only)
            if (this.variantNameElement && this.currentVariant.title) {
                this.variantNameElement.textContent = this.currentVariant.title;
            }
            
            // Update image if variant has featured_image
            if (this.imageElement && this.currentVariant.featured_image) {
                const newSrc = this.currentVariant.featured_image.src.replace(/(\.[^.]+)$/, '_200x200$1');
                this.imageElement.src = newSrc;
                this.imageElement.alt = this.currentVariant.featured_image.alt || '';
            }
            
            // Helper to convert hex to rgb
            const hexToRgb = (hex) => {
                const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
                return result ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}` : null;
            };
            
            // Update all buttons
            this.buttons.forEach(button => {
                // Reset custom disabled colors first
                button.style.removeProperty('--agp-btn-disabled-bg');
                button.style.removeProperty('--agp-btn-disabled-text');
                button.classList.remove('is-max-in-cart');
                
                // Remove notification custom classes if previously added
                if (this.notificationClasses) {
                    this.notificationClasses.forEach(cls => button.classList.remove(cls));
                }
                
                if (isMaxInCart) {
                    // Max in cart state
                    button.disabled = true;
                    button.classList.add('is-disabled');
                    button.classList.add('is-max-in-cart');
                    
                    // Apply custom disabled colors if set
                    const disabledBg = this.dataset.disabledBg;
                    const disabledText = this.dataset.disabledText;
                    if (disabledBg) {
                        const rgb = hexToRgb(disabledBg);
                        if (rgb) button.style.setProperty('--agp-btn-disabled-bg', rgb);
                    }
                    if (disabledText) {
                        const rgb = hexToRgb(disabledText);
                        if (rgb) button.style.setProperty('--agp-btn-disabled-text', rgb);
                    }
                } else if (isAvailable) {
                    button.disabled = false;
                    button.classList.remove('is-disabled');
                } else if (enableBackInStock) {
                    button.disabled = false;
                    button.classList.remove('is-disabled');
                    // Add notification custom classes
                    if (this.notificationClasses) {
                        this.notificationClasses.forEach(cls => button.classList.add(cls));
                    }
                } else {
                    button.disabled = true;
                    button.classList.add('is-disabled');
                }
            });
            
            // Update all button text elements
            this.buttonTextElements.forEach(textEl => {
                const button = textEl.closest('[data-sticky-atc-button]');
                if (button) {
                    if (isMaxInCart) {
                        textEl.textContent = button.dataset.maxInCartText || 'Max quantity reached';
                    } else if (isAvailable) {
                        // Use preorder text if this is a preorder product
                        if (this.isPreorder && button.dataset.preorderText) {
                            textEl.textContent = button.dataset.preorderText;
                        } else {
                            textEl.textContent = button.dataset.addText;
                        }
                    } else if (enableBackInStock) {
                        textEl.textContent = button.dataset.backInStockText;
                    } else {
                        textEl.textContent = button.dataset.soldOutText;
                    }
                }
            });
            
            // Update price visibility based on state
            this.updatePriceVisibility(isMaxInCart, isAvailable, enableBackInStock);
        }
        
        updatePriceVisibility(isMaxInCart, isAvailable, enableBackInStock) {
            // Determine which price settings to use based on state
            let showPriceDesktop = false;
            let showPriceMobile = false;
            
            if (isMaxInCart) {
                showPriceDesktop = this.dataset.showPriceMaxQtyDesktop === 'true';
                showPriceMobile = this.dataset.showPriceMaxQtyMobile === 'true';
            } else if (isAvailable) {
                if (this.isPreorder) {
                    showPriceDesktop = this.dataset.showPricePreorderDesktop === 'true';
                    showPriceMobile = this.dataset.showPricePreorderMobile === 'true';
                } else {
                    showPriceDesktop = this.dataset.showPriceDesktop === 'true';
                    showPriceMobile = this.dataset.showPriceMobile === 'true';
                }
            } else if (enableBackInStock) {
                showPriceDesktop = this.dataset.showPriceNotificationDesktop === 'true';
                showPriceMobile = this.dataset.showPriceNotificationMobile === 'true';
            }
            // For sold out (disabled), we use false by default
            
            // Find all price wrappers and update visibility
            // Desktop layout has class agp-sm-hidden (hidden on mobile)
            // Mobile content has class agp-lg-hidden (hidden on desktop)
            this.querySelectorAll('[data-price-wrapper]').forEach(wrapper => {
                const button = wrapper.closest('[data-sticky-atc-button]');
                if (!button) return;
                
                // Check if this is in desktop or mobile content
                const isInDesktopContent = wrapper.closest('.agp-sm-hidden') !== null;
                const isInMobileContent = wrapper.closest('.agp-lg-hidden') !== null;
                
                let shouldShow = false;
                if (isInDesktopContent) {
                    shouldShow = showPriceDesktop;
                } else if (isInMobileContent) {
                    shouldShow = showPriceMobile;
                } else {
                    // Neither - likely in a layout that shows on both
                    // Use desktop setting as default
                    shouldShow = showPriceDesktop;
                }
                
                wrapper.style.display = shouldShow ? '' : 'none';
            });
        }
        
        handleClickEffect(button, event) {
            const clickEffect = button.dataset.clickEffect;
            
            if (!clickEffect || clickEffect === 'none') return;
            
            switch (clickEffect) {
                case 'ripple':
                    // Create ripple element at click position
                    const rect = button.getBoundingClientRect();
                    const x = event.clientX - rect.left;
                    const y = event.clientY - rect.top;
                    const size = Math.max(rect.width, rect.height) * 2;
                    
                    const ripple = document.createElement('span');
                    ripple.className = 'agp-btn__ripple';
                    ripple.style.width = ripple.style.height = `${size}px`;
                    ripple.style.left = `${x - size / 2}px`;
                    ripple.style.top = `${y - size / 2}px`;
                    
                    button.appendChild(ripple);
                    
                    // Remove ripple after animation
                    ripple.addEventListener('animationend', () => ripple.remove());
                    break;
                    
                case 'bounce':
                    // Add and remove class for bounce animation
                    button.classList.add('is-clicked');
                    button.addEventListener('animationend', () => {
                        button.classList.remove('is-clicked');
                    }, { once: true });
                    break;
                    
                // scale, press, flash are handled purely by CSS :active state
            }
        }
        
        async onButtonClick(event) {
            event.preventDefault();
            
            const button = event.currentTarget;
            
            // Handle click effects
            this.handleClickEffect(button, event);
            
            if (this.isLoading) return;
            
            const variantId = this.currentVariant?.id;
            const isAvailable = this.currentVariant?.available ?? true;
            const enableBackInStock = this.dataset.enableBackInStock === 'true';
            
            // Check max-in-cart state (use String key for JSON lookup)
            const inventoryData = this.variantInventories[String(variantId)];
            const cartQty = this.cartQuantities[variantId] || 0;
            let isMaxInCart = false;
            
            if (inventoryData && isAvailable) {
                if (inventoryData.inventory_policy === 'deny') {
                    const maxQty = inventoryData.inventory_quantity;
                    if (cartQty >= maxQty && maxQty > 0) {
                        isMaxInCart = true;
                    }
                }
            }
            
            // Block if max in cart reached
            if (isMaxInCart) return;
            
            // Handle back in stock
            if (!isAvailable && enableBackInStock) {
                this.openBackInStockPopup();
                return;
            }
            
            if (!isAvailable) return;
            
            // Add to cart
            this.isLoading = true;
            this.classList.add('is-loading');
            
            try {
                const resolvedVariantId = variantId || this.variantSelects[0]?.value || 
                    this.formElement?.querySelector('input[name="id"]')?.value;
                
                if (!resolvedVariantId) {
                    console.error('AGP Sticky ATC: No variant ID found');
                    this.isLoading = false;
                    this.classList.remove('is-loading');
                    return;
                }
                
                const response = await fetch('/cart/add.js', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json'
                    },
                    body: JSON.stringify({
                        items: [{
                            id: parseInt(resolvedVariantId),
                            quantity: 1
                        }]
                    })
                });
                
                if (!response.ok) {
                    throw new Error('Failed to add to cart');
                }
                
                const data = await response.json();
                
                // Dispatch event for cart drawer/notification
                document.dispatchEvent(new CustomEvent('cart:refresh'));
                
                // Try to open cart drawer if it exists
                const cartDrawer = document.querySelector('cart-drawer');
                if (cartDrawer && typeof cartDrawer.show === 'function') {
                    cartDrawer.show();
                } else {
                    // Trigger Shopify's native cart update event
                    document.dispatchEvent(new CustomEvent('cart:add', { detail: data }));
                }
                
            } catch (error) {
                console.error('AGP Sticky ATC: Error adding to cart', error);
            } finally {
                this.isLoading = false;
                this.classList.remove('is-loading');
            }
        }
        
        openBackInStockPopup() {
            // Try to find and open the theme's back in stock popup if it exists
            const backInStockPopup = document.querySelector('[data-back-in-stock-popup], .back-in-stock-popup, back-in-stock-popup');
            if (backInStockPopup) {
                if (typeof backInStockPopup.show === 'function') {
                    backInStockPopup.show();
                } else {
                    backInStockPopup.classList.add('is-visible');
                }
                return;
            }
            
            // Fallback: dispatch event for other components to handle
            document.dispatchEvent(new CustomEvent('back-in-stock:open', {
                detail: { variantId: this.currentVariant?.id }
            }));
        }
        
        formatMoney(cents) {
            const formatter = new Intl.NumberFormat(Shopify.locale || 'en', {
                style: 'currency',
                currency: Shopify.currency?.active || 'EUR'
            });
            let formatted = formatter.format(cents / 100);
            // Append currency code if theme setting enables it (matches Shopify's money_with_currency filter)
            if (this.dataset.currencyCodeEnabled === 'true') {
                const code = Shopify.currency?.active || 'EUR';
                if (!formatted.includes(code)) {
                    formatted += ' ' + code;
                }
            }
            return formatted;
        }
    }
    
    // Register sticky ATC custom element
    if (!customElements.get('agp-sticky-atc')) {
        customElements.define('agp-sticky-atc', AgpStickyAtc);
    }

    /**
     * Fix product card badges: ensure the sold-out badge is visible alongside
     * custom badges. The theme's product-badges.liquid hides the sold-out badge
     * when a coming_soon metafield is present, but we want both to show.
     * Appends the sold-out badge after all other badges so it appears below.
     * 
     * Uses a MutationObserver to also fix badges in dynamically loaded content
     * (facet filtering, search results, pagination, predictive search).
     */
    function fixBadgeList(badgeList) {
        const soldOutBadge = badgeList.querySelector('sold-out-badge[hidden]');
        if (!soldOutBadge) return;

        const hasCustomBadge = badgeList.querySelector('.badge--custom, .badge--coming-soon');
        if (!hasCustomBadge) return;

        // Move sold-out badge to end (renders below custom badges in vertical layout)
        badgeList.appendChild(soldOutBadge);
        soldOutBadge.removeAttribute('hidden');
        badgeList.removeAttribute('hidden');
    }

    function initProductCardBadges() {
        document.querySelectorAll('badge-list').forEach(fixBadgeList);

        // Observe DOM for dynamically inserted badge-lists (facet updates, search, pagination)
        new MutationObserver(mutations => {
            for (const mutation of mutations) {
                for (const node of mutation.addedNodes) {
                    if (node.nodeType !== Node.ELEMENT_NODE) continue;
                    if (node.matches?.('badge-list')) {
                        fixBadgeList(node);
                    } else {
                        node.querySelectorAll?.('badge-list').forEach(fixBadgeList);
                    }
                }
            }
        }).observe(document.body, { childList: true, subtree: true });
    }

    /**
     * =========================================================================
     * AGP Card Carousel — Shared Module-Level Resources
     * =========================================================================
     * Performance: collections can render hundreds of carousels via infinite
     * scroll. Per-instance document listeners, matchMedia listeners and
     * IntersectionObservers add up quickly. These shared singletons replace
     * per-instance handlers and dispatch to a registry of registered
     * carousels instead.
     */
    // Registry of carousels with [data-reset-others] — used by the single
    // document-level coordinator that resets siblings when one is navigated.
    const __agpResetOthersRegistry = new Set();
    // Registry of all live carousels — subscribed to the shared matchMedia
    // listener for breakpoint changes.
    const __agpBpSubscribers = new Set();
    const __agpDesktopMql = window.matchMedia('(min-width: 1000px)');
    __agpDesktopMql.addEventListener('change', () => {
        __agpBpSubscribers.forEach((c) => {
            try { c.onBreakpointChange(); } catch (e) { /* noop */ }
        });
    });
    // Shared IntersectionObserver for the carousel "hint" peek animation.
    // Each carousel registers itself; the observer dispatches tryHint() on
    // the matching instance when the threshold is reached.
    const __agpHintMap = new WeakMap();
    const __agpHintObserver = ('IntersectionObserver' in window)
        ? new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting && entry.intersectionRatio >= 0.9) {
                    const c = __agpHintMap.get(entry.target);
                    if (c) c.tryHint();
                }
            });
        }, { threshold: 0.9 })
        : null;

    // Shared IntersectionObserver that *activates* carousels only once they
    // come close to the viewport. Long product lists (200+ items) instantiate
    // many <agp-card-carousel> elements; doing the full init (event listeners,
    // dot setup, hint registration) eagerly on every one of them is wasteful
    // and noticeable on slower devices. 600px of rootMargin is plenty for the
    // listener/dot setup (cheap, layout-free); it used to be 1500px when
    // activation also preloaded neighbour slide images, which fetched ~2
    // extra images per card at page load — heavy enough to evict the page
    // from the back/forward cache. Neighbour preloads now happen on first
    // interaction (see _primeNeighbors), so the band only needs to beat the
    // scroll, not the network.
    // AbortController gates the deactivate half of the lifecycle: it's what
    // lets us remove every listener in one call. Where it's unavailable (very
    // old browsers — which also lack content-visibility), we keep the original
    // activate-once behaviour and never deactivate, so nothing double-binds.
    const __agpCanAbort = typeof AbortController === 'function';
    const __agpActivateObserver = ('IntersectionObserver' in window)
        ? new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                const el = entry.target;
                // Bidirectional: activate as the carousel nears the viewport,
                // deactivate once it scrolls far away. Long grids never unmount
                // cards, so without the deactivate half the listener count and
                // per-instance state grow without bound. Keep observing across
                // cycles (no unobserve) so it can toggle back and forth.
                if (entry.isIntersecting) {
                    if (typeof el._activate === 'function') el._activate();
                } else if (__agpCanAbort && typeof el._deactivate === 'function') {
                    el._deactivate();
                }
            });
        }, { rootMargin: '600px 0px' })
        : null;

    /**
     * =========================================================================
     * AGP Card Carousel Custom Element
     * =========================================================================
     * Horizontal image carousel for AGP product cards.
     *
     * Features:
     * - Touch swipe on mobile, chevron arrows on desktop hover
     * - Transform-based sliding (no scroll-snap, full animation control)
     * - Lazy preload: loads next image when user swipes to adjacent slide
     * - "Hint" animation: 20% peek of second image on first view
     * - Respects display:none slides (metafield visibility per device)
     * - Recalculates on breakpoint change (matchMedia listener)
     *
     * Attributes:
     * - data-hint-desktop: Enable hint animation on desktop
     * - data-hint-mobile: Enable hint animation on mobile
     */
    if (!customElements.get('agp-card-carousel')) {
        customElements.define('agp-card-carousel', class extends HTMLElement {
            connectedCallback() {
                this.track = this.querySelector('.agp-card-carousel__track');
                if (!this.track) return;

                // Defer the (relatively expensive) full init until the carousel
                // is within the activate observer's rootMargin. For long
                // grids this means most carousels do essentially no work on
                // page load. If the observer isn't available, activate now.
                if (__agpActivateObserver) {
                    __agpActivateObserver.observe(this);
                } else {
                    this._activate();
                }
            }

            _activate() {
                if (this._activated) return;
                this._activated = true;

                // All listeners attached below are tied to this controller's
                // signal so _deactivate() can remove them in one call when the
                // carousel scrolls far off-screen.
                this._ac = __agpCanAbort ? new AbortController() : null;
                const signal = this._ac ? this._ac.signal : undefined;

                this.allSlides = [...this.track.children];
                // Capture the pristine DOM order once: loop rotation mutates
                // child order and _activate()/_deactivate() can run repeatedly.
                if (!this.originalSlides) this.originalSlides = [...this.allSlides];
                this.index = 0;
                // Preserve "already hinted" across activate/deactivate cycles so
                // the peek affordance doesn't replay each time a card re-enters.
                if (this.hinted === undefined) this.hinted = false;
                this.wasSwiping = false;
                this.loop = this.hasAttribute('data-loop');
                this.videoPauseOffscreen = this.hasAttribute('data-video-pause-offscreen');
                this.resetOthers = this.hasAttribute('data-reset-others');

                // Arrow navigation
                const prevBtn = this.querySelector('.agp-card-carousel__arrow--prev');
                const nextBtn = this.querySelector('.agp-card-carousel__arrow--next');
                if (prevBtn) prevBtn.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); this.userNav('prev'); }, { signal });
                if (nextBtn) nextBtn.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); this.userNav('next'); }, { signal });

                // Touch swipe
                this.track.addEventListener('touchstart', (e) => this.onTouchStart(e), { passive: true, signal });
                this.track.addEventListener('touchmove', (e) => this.onTouchMove(e), { passive: false, signal });
                this.track.addEventListener('touchend', (e) => this.onTouchEnd(e), { passive: true, signal });

                // Mouse drag swipe (desktop)
                this.track.addEventListener('mousedown', (e) => this.onMouseDown(e), { signal });
                this._onMouseMove = (e) => this.onMouseMove(e);
                this._onMouseUp = (e) => this.onMouseUp(e);

                // Prevent native image drag inside the carousel
                this.track.addEventListener('dragstart', (e) => e.preventDefault(), { signal });

                // Swallow the click that a finished mouse drag fires on the
                // slide anchor. Capture phase so it runs before the anchor acts.
                // Touch never reaches here (no synthetic click after a cancelled
                // touchmove) — the reset in onTouchStart is what keeps taps alive.
                this.track.addEventListener('click', (e) => {
                    if (this.wasSwiping) {
                        e.preventDefault();
                        this.wasSwiping = false;
                    }
                }, { capture: true, signal });

                // Breakpoint change: recalculate when metafield slides show/hide.
                // Uses a single shared matchMedia listener (see module top).
                __agpBpSubscribers.add(this);

                // Reset-others: register in shared coordinator instead of
                // adding a per-instance document listener.
                if (this.resetOthers) {
                    __agpResetOthersRegistry.add(this);
                }

                // Preload initial slide (needed for deferred videos at index 0)
                this.preloadSlide(this.index);
                // Neighbour slides are primed on FIRST INTERACTION, not at
                // activation. Preloading them here fetched 2 extra images per
                // card as soon as a card entered the activate band — ~30
                // requests / several MB per collection page — which kept the
                // network busy long enough to evict the page from the
                // back/forward cache (NetworkExceedsBufferLimit), turning
                // every PDP->back into a full reload. Hover/touch/focus cover
                // the common entry paths; tryHint() primes itself, and goTo()
                // preloads its own target for the paths that bypass all of
                // these (dot taps — the dots are a sibling of the carousel —
                // and pointers already resting on the card at activation).
                this._neighborsPrimed = false;
                const prime = () => this._primeNeighbors();
                this.addEventListener('pointerenter', prime, { once: true, signal });
                this.addEventListener('focusin', prime, { once: true, signal });
                this.track.addEventListener('touchstart', prime, { once: true, passive: true, signal });

                // Set initial arrow state
                this.updateArrows();

                // Dots navigation (mobile-only, rendered as sibling after the
                // carousel inside .product-card__figure). Buttons follow the
                // same slide order; visibility classes on the buttons must
                // mirror the slide visibility classes so visibleDots maps 1:1
                // to visibleSlides.
                this.setupDots();

                // Play button on non-autoplay videos (click plays video, doesn't navigate)
                this.querySelectorAll('.agp-card-carousel__play').forEach(btn => {
                    btn.addEventListener('click', (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        const slide = btn.closest('.agp-card-carousel__slide--video');
                        const video = slide && slide.querySelector('video');
                        if (video) {
                            this.preloadSlide(this.visibleSlides.indexOf(slide));
                            video.play().catch(() => {});
                            btn.style.display = 'none';
                        }
                    }, { signal });
                });

                // Hint animation via IntersectionObserver
                this.setupHint();
            }

            disconnectedCallback() {
                if (__agpActivateObserver) __agpActivateObserver.unobserve(this);
                this._deactivate();
            }

            /**
             * Tear down everything _activate() set up. Called when the carousel
             * scrolls far off-screen (it stays in the DOM — long grids never
             * unmount cards) and on disconnect. Removes all listeners, leaves
             * the shared registries, stops the hint, pauses video, and resets a
             * swiped carousel to slide 0 so re-entry re-activates from scratch.
             * Performs no layout reads, so it is safe to run while the card is
             * content-visibility:skipped.
             */
            _deactivate() {
                if (!this._activated) return;
                this._activated = false;

                // Remove every listener attached during _activate()/setupDots()
                // (arrows, track touch/mouse/drag/click, dots, play buttons).
                if (this._ac) { this._ac.abort(); this._ac = null; }
                // Defensively drop any in-flight document drag listeners.
                if (this._onMouseMove) document.removeEventListener('mousemove', this._onMouseMove);
                if (this._onMouseUp) document.removeEventListener('mouseup', this._onMouseUp);
                this.mouseDown = false;

                // Leave shared coordinators so they stop iterating this instance.
                __agpBpSubscribers.delete(this);
                __agpResetOthersRegistry.delete(this);

                // Stop the hint machinery and any pending timer.
                if (__agpHintObserver) {
                    __agpHintObserver.unobserve(this);
                    __agpHintMap.delete(this);
                }
                if (this._hintTimer) { clearTimeout(this._hintTimer); this._hintTimer = null; }

                // Pause any playing video to release its decode pipeline/CPU.
                this.querySelectorAll('video').forEach((v) => {
                    if (!v.paused) { try { v.pause(); } catch (e) { /* noop */ } }
                });

                // Reset a swiped carousel back to slide 0 (matches the
                // reset-others behaviour). Slide 0 is always at offset 0, so we
                // reposition with translateX(0) and never read offsetLeft —
                // important because the card may be content-visibility:skipped.
                if (this.index !== 0 && this.originalSlides && this.track) {
                    this.originalSlides.forEach((s) => this.track.append(s));
                    this.allSlides = [...this.track.children];
                    this._invalidateVisibleCache();
                    this.index = 0;
                    this.track.style.transition = 'none';
                    this.track.style.transform = 'translateX(0)';
                    this.track.style.transition = '';
                    this.updateArrows();
                    this.updateDots();
                }
            }

            /**
             * Load the slides adjacent to the current one (and the loop-wrap
             * last slide) so the neighbour image/video is ready on first
             * swipe in either direction. Runs once per activation, on the
             * first interaction with the card (pointerenter/focusin/
             * touchstart) or when the hint animation is about to play.
             */
            _primeNeighbors() {
                if (this._neighborsPrimed) return;
                this._neighborsPrimed = true;
                this.preloadSlide(this.index + 1);
                if (this.index > 0) {
                    this.preloadSlide(this.index - 1);
                } else if (this.loop) {
                    const slides = this.visibleSlides;
                    if (slides.length > 1) this.preloadSlide(slides.length - 1);
                }
            }

            /** Invalidate cached visibleSlides/visibleDots (call after order or display changes) */
            _invalidateVisibleCache() {
                this._visibleSlidesCache = null;
                this._visibleDotsCache = null;
            }

            /** Get only visible (not display:none) slides — cached */
            get visibleSlides() {
                if (!this._visibleSlidesCache) {
                    this._visibleSlidesCache = this.allSlides.filter(s => getComputedStyle(s).display !== 'none');
                }
                return this._visibleSlidesCache;
            }

            /* ----- Navigation ----- */

            /** Navigate to a slide by visible index */
            goTo(visibleIndex) {
                const slides = this.visibleSlides;
                if (visibleIndex < 0 || visibleIndex >= slides.length) return;

                // Pause video on the slide we're leaving
                if (this.videoPauseOffscreen && visibleIndex !== this.index) {
                    const leaving = slides[this.index];
                    if (leaving) {
                        const vid = leaving.querySelector('video');
                        if (vid && !vid.paused) vid.pause();
                    }
                }

                this.index = visibleIndex;
                // Load the target slide itself: navigation can originate from
                // paths that never fire the once-prime listeners (dots are a
                // SIBLING of the carousel, and a pointer already resting on
                // the card at activation gets no pointerenter), so with
                // neighbour preloads deferred to first interaction the target
                // may still be src-less here. preloadSlide is idempotent.
                this.preloadSlide(visibleIndex);
                const offset = slides[visibleIndex].offsetLeft;
                this.track.style.transform = `translateX(-${offset}px)`;
                this.preloadSlide(visibleIndex + 1);
                this.preloadSlide(visibleIndex - 1);

                // When looping, also preload the wrap-around slide so it's ready
                // for the next swipe at the boundary (avoids loading during drag)
                if (this.loop) {
                    if (visibleIndex === 0) this.preloadSlide(slides.length - 1);
                    if (visibleIndex === slides.length - 1) this.preloadSlide(0);
                }

                this.updateArrows();

                // Resume video on the slide we're arriving at
                if (this.videoPauseOffscreen) {
                    const arriving = slides[visibleIndex];
                    if (arriving) {
                        const vid = arriving.querySelector('video');
                        if (vid && vid.paused && vid.dataset.loaded) {
                            vid.play().catch(() => {});
                        }
                    }
                }
                this.updateDots();
            }

            /* ----- Dots ----- */

            /** Find dots container (sibling after the carousel, inside figure). */
            setupDots() {
                const parent = this.parentElement;
                this.dots = parent ? parent.querySelector('.agp-card-carousel__dots') : null;
                if (!this.dots) return;
                this.allDots = [...this.dots.querySelectorAll('button')];
                this.allDots.forEach((btn) => {
                    btn.addEventListener('click', (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        const idx = this.visibleDots.indexOf(btn);
                        if (idx >= 0) this.goTo(idx);
                    }, { signal: this._ac ? this._ac.signal : undefined });
                });
                this.updateDots();
            }

            /** Visible dots (filter out display:none, matches visibleSlides). Cached. */
            get visibleDots() {
                if (!this.allDots) return [];
                if (!this._visibleDotsCache) {
                    this._visibleDotsCache = this.allDots.filter((b) => getComputedStyle(b).display !== 'none');
                }
                return this._visibleDotsCache;
            }

            /** Sync aria-current on dot buttons with current slide index. */
            updateDots() {
                if (!this.allDots) return;
                this.visibleDots.forEach((btn, i) => {
                    btn.setAttribute('aria-current', i === this.index ? 'true' : 'false');
                });
            }

            prev() {
                const slides = this.visibleSlides;
                if (this.index > 0) {
                    this.goTo(this.index - 1);
                } else if (this.loop && slides.length > 1) {
                    this.rotatePrev();
                }
            }

            next() {
                const slides = this.visibleSlides;
                if (this.index < slides.length - 1) {
                    this.goTo(this.index + 1);
                } else if (this.loop && slides.length > 1) {
                    this.rotateNext();
                }
            }

            /* ----- Infinite loop via DOM reordering ----- */

            /**
             * Restore slides to their original DOM order and reset index.
             * Used before breakpoint recalc and fadeReset to undo any rotation.
             */
            restoreOrder() {
                const currentSlide = this.visibleSlides[this.index];
                this.originalSlides.forEach(s => this.track.append(s));
                this.allSlides = [...this.track.children];
                this._invalidateVisibleCache();
                // Find where the current slide ended up after restore
                const slides = this.visibleSlides;
                const newIdx = slides.indexOf(currentSlide);
                this.index = newIdx >= 0 ? newIdx : 0;
            }

            /**
             * Move the last visible slide to the front of the track,
             * keep current slide visually stationary, then animate to the moved slide.
             */
            rotatePrev() {
                const slides = this.visibleSlides;
                const lastSlide = slides[slides.length - 1];

                // Preload the slide we're about to show
                this.preloadSlide(slides.length - 1);

                // Move last slide to the front of the DOM
                this.track.prepend(lastSlide);
                this.allSlides = [...this.track.children];
                this._invalidateVisibleCache();

                // Current slide shifted from index 0 to index 1 — reposition instantly
                this.index = 1;
                this.track.style.transition = 'none';
                this.track.style.transform = `translateX(-${this.visibleSlides[1].offsetLeft}px)`;
                this.track.offsetHeight; // reflow
                this.track.style.transition = '';

                // Animate to index 0 (the moved slide)
                this.goTo(0);
            }

            /**
             * Move the first visible slide to the end of the track,
             * keep current slide visually stationary, then animate to the moved slide.
             */
            rotateNext() {
                const slides = this.visibleSlides;
                const firstSlide = slides[0];

                // Preload the slide we're about to show
                this.preloadSlide(0);

                // Move first slide to the end of the DOM
                this.track.append(firstSlide);
                this.allSlides = [...this.track.children];
                this._invalidateVisibleCache();

                // Current slide shifted from last to second-to-last — reposition instantly
                const newSlides = this.visibleSlides;
                this.index = newSlides.length - 2;
                this.track.style.transition = 'none';
                this.track.style.transform = `translateX(-${newSlides[this.index].offsetLeft}px)`;
                this.track.offsetHeight; // reflow
                this.track.style.transition = '';

                // Animate to last index (the moved slide)
                this.goTo(newSlides.length - 1);
            }

            /** User-initiated navigation (arrows / swipe). Triggers reset on others. */
            userNav(dir) {
                this.cancelHint();
                if (dir === 'prev') this.prev();
                else this.next();
                if (this.resetOthers && __agpResetOthersRegistry.size > 1) {
                    // Direct iteration of the registry replaces a document-
                    // wide CustomEvent broadcast. Skip carousels that are far
                    // off-screen to avoid touching DOM for elements the user
                    // can't see.
                    const vh = window.innerHeight;
                    __agpResetOthersRegistry.forEach((c) => {
                        if (c === this) return;
                        const r = c.getBoundingClientRect();
                        if (r.bottom < -200 || r.top > vh + 200) return;
                        c.fadeReset();
                    });
                }
            }

            /** Soft reset: restore order, overlay first image, reposition, remove overlay */
            fadeReset() {
                // Restore original order so slides[0] is the real first slide
                this.restoreOrder();

                if (this.index === 0) return;

                // Pause any playing video on the current slide
                if (this.videoPauseOffscreen) {
                    const slides = this.visibleSlides;
                    const current = slides[this.index];
                    if (current) {
                        const vid = current.querySelector('video');
                        if (vid && !vid.paused) vid.pause();
                    }
                }

                // Reposition track to current slide (order just changed)
                const slides = this.visibleSlides;
                this.track.style.transition = 'none';
                this.track.style.transform = `translateX(-${slides[this.index].offsetLeft}px)`;
                this.track.offsetHeight;
                this.track.style.transition = '';

                const firstSlide = slides[0];
                if (!firstSlide) return;

                // Create overlay from first slide's image/poster
                const img = firstSlide.querySelector('img');
                const video = firstSlide.querySelector('video');
                const overlay = document.createElement('div');
                Object.assign(overlay.style, {
                    position: 'absolute', inset: '0', zIndex: '3',
                    opacity: '0', transition: 'opacity 0.3s ease',
                    pointerEvents: 'none'
                });

                if (img) {
                    const clone = img.cloneNode(true);
                    clone.style.width = '100%';
                    clone.style.height = '100%';
                    clone.style.objectFit = 'cover';
                    overlay.appendChild(clone);
                } else if (video) {
                    const poster = document.createElement('img');
                    poster.src = video.getAttribute('poster') || '';
                    Object.assign(poster.style, { width: '100%', height: '100%', objectFit: 'cover' });
                    overlay.appendChild(poster);
                }

                this.appendChild(overlay);
                overlay.offsetHeight; // reflow

                // Fade overlay in
                overlay.style.opacity = '1';

                const afterFadeIn = () => {
                    overlay.removeEventListener('transitionend', afterFadeIn);

                    // Jump track to first slide (hidden behind overlay)
                    this.index = 0;
                    const offset = slides[0] ? slides[0].offsetLeft : 0;
                    this.track.style.transition = 'none';
                    this.track.style.transform = `translateX(-${offset}px)`;
                    this.track.offsetHeight; // reflow
                    this.track.style.transition = '';
                    this.updateArrows();

                    // Fade overlay out to reveal real first slide
                    overlay.style.transition = 'opacity 0.3s ease';
                    overlay.style.opacity = '0';

                    const afterFadeOut = () => {
                        overlay.removeEventListener('transitionend', afterFadeOut);
                        overlay.remove();
                    };
                    overlay.addEventListener('transitionend', afterFadeOut);
                };
                overlay.addEventListener('transitionend', afterFadeIn);
            }

            /** Show/hide arrows based on boundary when loop is disabled */
            updateArrows() {
                if (this.loop) {
                    this.removeAttribute('data-at-start');
                    this.removeAttribute('data-at-end');
                    return;
                }
                const slides = this.visibleSlides;
                if (this.index <= 0) this.setAttribute('data-at-start', '');
                else this.removeAttribute('data-at-start');
                if (this.index >= slides.length - 1) this.setAttribute('data-at-end', '');
                else this.removeAttribute('data-at-end');
            }

            /** Load deferred images/videos for a visible slide index */
            preloadSlide(visibleIndex) {
                const slides = this.visibleSlides;
                if (visibleIndex < 0 || visibleIndex >= slides.length) return;
                const slide = slides[visibleIndex];

                // Handle deferred images (data-src)
                const img = slide.querySelector('img[data-src]');
                if (img) {
                    img.src = img.dataset.src;
                    if (img.dataset.srcset) img.srcset = img.dataset.srcset;
                    if (img.dataset.sizes) img.sizes = img.dataset.sizes;
                    delete img.dataset.src;
                    delete img.dataset.srcset;
                    delete img.dataset.sizes;
                }

                // Force lazy images to load eagerly (carousel transforms move them
                // off-screen, preventing browser lazy loading from triggering)
                const lazyImg = slide.querySelector('img[loading="lazy"]:not(.agp-slide-lqip)');
                if (lazyImg) {
                    lazyImg.loading = 'eager';
                }

                // Handle deferred videos
                const video = slide.querySelector('video');
                if (video && !video.dataset.loaded) {
                    video.querySelectorAll('source[data-src]').forEach(source => {
                        source.src = source.dataset.src;
                        delete source.dataset.src;
                    });
                    video.load();
                    video.dataset.loaded = 'true';
                    if (video.hasAttribute('autoplay')) {
                        video.play().catch(() => {});
                    }
                }
            }

            /* ----- Mouse drag handling (desktop) ----- */
            onMouseDown(e) {
                if (e.button !== 0) return; // left click only
                this.wasSwiping = false; // see onTouchStart
                this.cancelHint();
                this.startX = e.clientX;
                this.startY = e.clientY;
                this.dragDelta = 0;
                this.isDragging = false;
                this.isScrolling = null;
                this.rotatedForDrag = false;
                this.mouseDown = true;
                this.track.classList.add('agp-card-carousel__track--dragging');
                document.addEventListener('mousemove', this._onMouseMove);
                document.addEventListener('mouseup', this._onMouseUp);
            }

            onMouseMove(e) {
                if (!this.mouseDown) return;
                const dx = e.clientX - this.startX;
                const dy = e.clientY - this.startY;

                if (this.isScrolling === null && (Math.abs(dx) > 5 || Math.abs(dy) > 5)) {
                    this.isScrolling = Math.abs(dy) > Math.abs(dx);
                }
                if (this.isScrolling) return;

                e.preventDefault();
                this.isDragging = true;
                this.dragDelta = dx;

                if (this.loop && !this.rotatedForDrag) {
                    const slides = this.visibleSlides;
                    if (dx > 0 && this.index === 0) {
                        const lastSlide = slides[slides.length - 1];
                        this.preloadSlide(slides.length - 1);
                        this.track.prepend(lastSlide);
                        this.allSlides = [...this.track.children];
                        this._invalidateVisibleCache();
                        this.index = 1;
                        this.rotatedForDrag = true;
                    } else if (dx < 0 && this.index === slides.length - 1) {
                        const firstSlide = slides[0];
                        this.preloadSlide(0);
                        this.track.append(firstSlide);
                        this.allSlides = [...this.track.children];
                        this._invalidateVisibleCache();
                        const newSlides = this.visibleSlides;
                        this.index = newSlides.length - 2;
                        this.rotatedForDrag = true;
                    }
                }

                const slides = this.visibleSlides;
                const base = slides[this.index] ? -slides[this.index].offsetLeft : 0;
                this.track.style.transform = `translateX(${base + dx}px)`;
            }

            onMouseUp() {
                if (!this.mouseDown) return;
                document.removeEventListener('mousemove', this._onMouseMove);
                document.removeEventListener('mouseup', this._onMouseUp);
                this.mouseDown = false;
                this.track.classList.remove('agp-card-carousel__track--dragging');
                if (this.isDragging && Math.abs(this.dragDelta) > 40) {
                    this.wasSwiping = true;
                    const slides = this.visibleSlides;
                    const swipingPrev = this.dragDelta > 0;
                    const swipingNext = this.dragDelta < 0;
                    if (!this.loop && swipingPrev && this.index <= 0) {
                        this.goTo(this.index);
                    } else if (!this.loop && swipingNext && this.index >= slides.length - 1) {
                        this.goTo(this.index);
                    } else if (swipingPrev) {
                        this.userNav('prev');
                    } else {
                        this.userNav('next');
                    }
                } else {
                    this.goTo(this.index);
                }
                this.startX = null;
                this.isDragging = false;
                this.isScrolling = null;
            }

            /* ----- Touch handling ----- */
            onTouchStart(e) {
                // Every new gesture starts clickable. wasSwiping exists to cancel
                // the click that a finished MOUSE drag fires on the anchor
                // underneath; a touch swipe fires no click at all, because
                // onTouchMove calls preventDefault() and that suppresses the
                // compatibility mouse events. So on mobile the flag outlived its
                // gesture and swallowed the user's NEXT tap: swipe the photo,
                // tap it, nothing happens — you had to tap a second time.
                this.wasSwiping = false;
                this.cancelHint();
                this.startX = e.touches[0].clientX;
                this.startY = e.touches[0].clientY;
                this.dragDelta = 0;
                this.isDragging = false;
                this.isScrolling = null;
                this.rotatedForDrag = false;
                this.track.classList.add('agp-card-carousel__track--dragging');
            }

            onTouchMove(e) {
                if (!this.startX) return;
                const dx = e.touches[0].clientX - this.startX;
                const dy = e.touches[0].clientY - this.startY;

                if (this.isScrolling === null && (Math.abs(dx) > 5 || Math.abs(dy) > 5)) {
                    this.isScrolling = Math.abs(dy) > Math.abs(dx);
                }
                // Wait until we've decided which axis dominates before doing
                // anything. Calling preventDefault() while the direction is
                // still unknown (isScrolling === null) blocks the page's
                // vertical scroll on iOS Safari for the rest of the gesture.
                if (this.isScrolling !== false) return;

                e.preventDefault();
                this.isDragging = true;
                this.dragDelta = dx;

                // Preemptive DOM rotation for loop: place a slide in the drag direction
                // so the user sees content instead of blank space during the drag
                if (this.loop && !this.rotatedForDrag) {
                    const slides = this.visibleSlides;
                    if (dx > 0 && this.index === 0) {
                        // Swiping right at first slide: move last to front
                        const lastSlide = slides[slides.length - 1];
                        this.preloadSlide(slides.length - 1);
                        this.track.prepend(lastSlide);
                        this.allSlides = [...this.track.children];
                        this._invalidateVisibleCache();
                        this.index = 1;
                        this.rotatedForDrag = true;
                    } else if (dx < 0 && this.index === slides.length - 1) {
                        // Swiping left at last slide: move first to end
                        const firstSlide = slides[0];
                        this.preloadSlide(0);
                        this.track.append(firstSlide);
                        this.allSlides = [...this.track.children];
                        this._invalidateVisibleCache();
                        const newSlides = this.visibleSlides;
                        this.index = newSlides.length - 2;
                        this.rotatedForDrag = true;
                    }
                }

                const slides = this.visibleSlides;
                const base = slides[this.index] ? -slides[this.index].offsetLeft : 0;
                this.track.style.transform = `translateX(${base + dx}px)`;
            }

            onTouchEnd() {
                this.track.classList.remove('agp-card-carousel__track--dragging');
                if (this.isDragging && Math.abs(this.dragDelta) > 40) {
                    this.wasSwiping = true;
                    const slides = this.visibleSlides;
                    const swipingPrev = this.dragDelta > 0;
                    const swipingNext = this.dragDelta < 0;
                    if (!this.loop && swipingPrev && this.index <= 0) {
                        this.goTo(this.index);
                    } else if (!this.loop && swipingNext && this.index >= slides.length - 1) {
                        this.goTo(this.index);
                    } else if (swipingPrev) {
                        this.userNav('prev');
                    } else {
                        this.userNav('next');
                    }
                } else {
                    this.goTo(this.index);
                }
                this.startX = null;
                this.isDragging = false;
                this.isScrolling = null;
            }

            /* ----- Breakpoint change ----- */
            onBreakpointChange() {
                // Display rules may have changed between desktop/mobile;
                // drop cached visibleSlides/visibleDots so they re-filter.
                this._invalidateVisibleCache();
                // Restore original DOM order before recalculating
                this.restoreOrder();
                const slides = this.visibleSlides;
                if (this.index >= slides.length) this.index = Math.max(0, slides.length - 1);
                this.track.style.transition = 'none';
                if (slides[this.index]) {
                    this.track.style.transform = `translateX(-${slides[this.index].offsetLeft}px)`;
                }
                this.track.offsetHeight;
                this.track.style.transition = '';
                this.updateArrows();
                this.updateDots();
            }

            /* ----- Hint animation ----- */
            setupHint() {
                const hintDesktop = this.hasAttribute('data-hint-desktop');
                const hintMobile = this.hasAttribute('data-hint-mobile');
                if (!hintDesktop && !hintMobile) return;

                this.hintDesktop = hintDesktop;
                this.hintMobile = hintMobile;

                // Register with the shared module-level IntersectionObserver
                // instead of allocating one per carousel.
                if (__agpHintObserver) {
                    __agpHintMap.set(this, this);
                    __agpHintObserver.observe(this);
                }
            }

            tryHint() {
                if (this.hinted) return;

                const isDesktop = window.matchMedia('(min-width: 1000px)').matches;
                if (isDesktop && !this.hintDesktop) return;
                if (!isDesktop && !this.hintMobile) return;

                const slides = this.visibleSlides;
                if (slides.length < 2) return;

                // The peek shows the next slide, whose image is no longer
                // preloaded at activation — give it a src now so the
                // load-completion checks below can succeed.
                this._primeNeighbors();

                // Check current and next slide images are loaded
                const imgs = slides.slice(this.index, this.index + 2)
                    .map(s => s.querySelector('img'))
                    .filter(Boolean);

                const allLoaded = imgs.every(img => img.complete && img.naturalWidth > 0);
                if (allLoaded) {
                    this.performHint();
                } else {
                    let loadCount = 0;
                    const check = () => {
                        loadCount++;
                        if (imgs.every(img => img.complete && img.naturalWidth > 0)) {
                            this.performHint();
                        }
                    };
                    imgs.forEach(img => {
                        if (!img.complete) img.addEventListener('load', check, { once: true });
                    });
                }
            }

            performHint() {
                if (this.hinted) return;
                this.hinted = true;
                if (__agpHintObserver) {
                    __agpHintObserver.unobserve(this);
                    __agpHintMap.delete(this);
                }

                const delay = parseInt(this.getAttribute('data-hint-delay')) || 0;

                this._hintTimer = setTimeout(() => {
                    this._hintTimer = null;
                    this.preloadSlide(this.index + 1);

                    const slides = this.visibleSlides;
                    const currentOffset = slides[this.index] ? slides[this.index].offsetLeft : 0;
                    const peekAmount = this.track.clientWidth * 0.2;

                    this.track.style.transition = 'transform 0.4s ease';
                    this.track.style.transform = `translateX(-${currentOffset + peekAmount}px)`;

                    setTimeout(() => {
                        this.track.style.transform = `translateX(-${currentOffset}px)`;
                        setTimeout(() => {
                            this.track.style.transition = '';
                        }, 400);
                    }, 500);
                }, delay);
            }

            /** Cancel any pending or in-progress hint animation */
            cancelHint() {
                this.hinted = true;
                if (__agpHintObserver) {
                    __agpHintObserver.unobserve(this);
                    __agpHintMap.delete(this);
                }
                if (this._hintTimer) {
                    clearTimeout(this._hintTimer);
                    this._hintTimer = null;
                }
                // performHint() sets an inline `transition: transform 0.4s ease`
                // to drive the peek animation. If the user starts a touch while
                // that inline style is still set, it overrides the
                // `.agp-card-carousel__track--dragging` CSS rule's
                // `transition: none`, so the boundary-rotation transform jump
                // in onTouchMove gets animated over 400ms — visually sliding
                // through every slide in between (flash of many images on
                // mobile, especially Chrome iOS). Clear it here.
                this.track.style.transition = '';
            }
        });
    }

    /**
     * AGP Infinite Scroll Custom Element
     * Observes its own visibility and fetches the next page of products,
     * appending them to the grid. Stops when no more pages.
     */
    if (!customElements.get('agp-infinite-scroll')) {
        customElements.define('agp-infinite-scroll', class extends HTMLElement {
            connectedCallback() {
                this.nextUrl = this.dataset.nextUrl;
                this.gridSelector = this.dataset.gridSelector;
                this.loading = false;
                this.manual = this.hasAttribute('data-manual');
                // When data-cols-d/m are set, they size the skeleton cells that
                // reserve the next page's space (appended cards keep their own
                // server-rendered --cols values).
                this.colsD = this.dataset.colsD || null;
                this.colsM = this.dataset.colsM || null;
                this._btn = this.querySelector('.agp-infinite-scroll__button');

                const grid = document.querySelector(this.gridSelector);

                // Scroll-progress pagination (collection/search only, via
                // data-track-page): mirrors the page the visitor is looking
                // at into ?page=N, wires the "Load previous" bar, and
                // re-centers the card they left through when they come back
                // from a product page. Runs even when there is no next URL
                // (deep link to the last page still needs the bar/tracking).
                this._trackPage = this.hasAttribute('data-track-page')
                    && !(window.Shopify && window.Shopify.designMode);
                this._currentPage = parseInt(this.dataset.currentPage, 10) || 1;
                if (grid && this._trackPage) this._initPageTracking(grid);

                if (!this.nextUrl) {
                    this.classList.add('agp-infinite-scroll--done');
                    return;
                }

                // Page 1 is always full when a next page exists, so its card
                // count IS the server page size — how many skeleton cells to
                // reserve per load.
                this._pageSize = grid ? grid.children.length : 0;
                // Absolute total_idx of the first card the next fetch will
                // bring, from the server (data-next-offset). Falls back to the
                // rendered card count, which is only equivalent when browsing
                // started on page 1 — deep links to ?page=N would desync the
                // skeleton column phase otherwise.
                const nextOffset = parseInt(this.dataset.nextOffset, 10);
                this._nextOffset = isNaN(nextOffset) ? this._pageSize : nextOffset;

                // The button drives manual mode AND the auto-mode fallback
                // (_showManualFallback), so it is always wired.
                if (this._btn) {
                    this._btn.addEventListener('click', () => this.loadMore());
                }
                if (this.manual) return;

                // Auto mode. CLS rule: content may only be added to the grid
                // while the insertion point sits BELOW the viewport — anything
                // else moves what the user is currently looking at and is
                // counted as a layout shift (this was the collection-page
                // field-CLS regression). Concretely:
                //   1. The trigger band is bottom-only: it opens below the
                //      viewport and CLOSES once the sentinel is visible.
                //      (The old '800px' rootMargin opened the band on ALL
                //      sides, so appends could fire with the sentinel above
                //      the viewport, shifting the whole page down.) The band
                //      is two-stage: 800px at rest — bigger bands prefetch
                //      page 2 on every initial load, costing a full HTML
                //      fetch per view and working against back/forward-cache
                //      eligibility — widened to 1600px by the scroll handler
                //      after the first scroll, when browsing intent is real
                //      and the head start halves the odds of ever seeing the
                //      grey skeletons. The IO below stays the 800px wake-up;
                //      the scroll fallback carries the widened band.
                //   2. loadMore() reserves the incoming page's space with
                //      skeleton cells at trigger time, while still off-screen,
                //      and swaps the real cards into that space when the fetch
                //      resolves — a ~net-zero height change even if the user
                //      has scrolled into the skeletons meanwhile.
                //   3. If the user outruns the band entirely (End key,
                //      scrollbar jump, back-navigation scroll restore), we do
                //      NOT auto-append above their viewport: the "Load more"
                //      button is revealed instead. That path is user-initiated
                //      (CLS-exempt) and can't runaway-load in the footer.
                this._band = 800;
                this.observer = new IntersectionObserver((entries) => {
                    entries.forEach(entry => {
                        if (!entry.isIntersecting) return;
                        this._autoTrigger(entry.boundingClientRect.top);
                    });
                }, { rootMargin: '0px 0px 800px 0px' });

                this.observer.observe(this);

                // Scroll fallback: IntersectionObserver can drop callbacks
                // during very fast scrolls (when the sentinel's isIntersecting
                // flips true→false within a single frame, the browser may only
                // report the final state). A rAF-throttled scroll handler
                // mirrors the band check.
                this._scrollScheduled = false;
                this._onScroll = () => {
                    if (this._scrollScheduled) return;
                    this._scrollScheduled = true;
                    requestAnimationFrame(() => {
                        this._scrollScheduled = false;
                        // First scroll = real browsing intent: widen the
                        // trigger band (see the band comment above). Fresh
                        // landings that never scroll never prefetch page 2.
                        this._band = 1600;
                        if (!this.nextUrl || this.loading) return;
                        const top = this.getBoundingClientRect().top;
                        if (this.manual) {
                            // Fallback-manual only (merchant data-manual never
                            // wires this listener). Re-arm auto mode once the
                            // sentinel is fully below the viewport again: the
                            // unsafe geometry that forced the fallback is gone,
                            // so the band rules apply exactly as on a fresh
                            // load. Without this the fallback was sticky for
                            // the page's whole life — one End-key jump meant
                            // clicking Load more forever after.
                            const viewportH = window.innerHeight || document.documentElement.clientHeight;
                            if (top > viewportH) {
                                this.manual = false;
                                if (this._btn) this._btn.hidden = true;
                                if (this.observer) this.observer.observe(this);
                                this._autoTrigger(top);
                            }
                            return;
                        }
                        this._autoTrigger(top);
                    });
                };
                window.addEventListener('scroll', this._onScroll, { passive: true });

                // Opened past the grid (back-navigation scroll restore after a
                // bfcache eviction, refresh in the footer): the sentinel is
                // already at/above the viewport and any automatic append would
                // shift the visible footer. Reveal the button instead of the
                // old fillUntilVisible() auto-load.
                requestAnimationFrame(() => {
                    if (!this.nextUrl || this.loading || this.manual) return;
                    this._autoTrigger(this.getBoundingClientRect().top);
                });
            }

            /**
             * Band decision for all auto-mode triggers (IO, scroll fallback,
             * mount check). `top` is the sentinel's boundingClientRect.top.
             */
            _autoTrigger(top) {
                if (!this.nextUrl || this.loading || this.manual) return;
                const viewportH = window.innerHeight || document.documentElement.clientHeight;
                if (top > viewportH && top <= viewportH + (this._band || 800)) {
                    // Approaching from below, still off-screen: safe to load.
                    this.loadMore();
                } else if (top <= viewportH) {
                    // Already visible or above the viewport: appending would
                    // shift visible content — hand over to the button.
                    this._showManualFallback();
                }
            }

            /**
             * Switch this sentinel to user-initiated loading: reveal the
             * "Load more" button and pause the automatic triggers. Loads stay
             * CLS-safe because loadMore() reserves the space synchronously in
             * the click's input window and the later card swap is ~net-zero.
             *
             * NOT permanent: the scroll listener stays alive and re-arms auto
             * mode as soon as the sentinel is fully below the viewport again
             * (see _onScroll) — otherwise a single End-key jump would demote
             * the whole session to click-to-load.
             */
            _showManualFallback() {
                this.manual = true;
                if (this._btn) this._btn.hidden = false;
                if (this.observer) this.observer.disconnect();
            }

            /**
             * Scroll-progress pagination. The field-CLS fix stopped auto-
             * appends above the viewport, which exposed the real UX gap:
             * a PDP visit -> back that misses the back/forward cache reloads
             * page 1 and the visitor loses their place in the list. Instead
             * we keep ?page=N in the URL synced to the page being LOOKED AT
             * (replaceState — no history spam), so the return trip lands on
             * the right server page, a "Load previous" bar lets them walk
             * back up, and a sessionStorage note re-centers the exact card
             * they left through. A bfcache HIT never re-runs any of this —
             * the whole DOM and scroll position come back as-is.
             */
            _initPageTracking(grid) {
                this._grid = grid;
                // Every server-rendered child belongs to the landing page:
                // the server renders exactly one paginate page per document.
                [...grid.children].forEach((el) => {
                    el.dataset.agpPage = String(this._currentPage);
                });
                // First cell of each loaded page, kept sorted by page number
                // (loadMore pushes, loadPrevious unshifts). The tracker walks
                // these — a handful of rect reads per throttled tick, not one
                // per card.
                this._pageBounds = grid.firstElementChild
                    ? [{ page: this._currentPage, el: grid.firstElementChild }]
                    : [];
                this._reportedPage = this._currentPage;

                // "Load previous" bar: rendered by agp-products-grid.liquid
                // as a preceding sibling of the grid on ?page>1 landings.
                // Progressive enhancement over its plain ?page=N-1 link.
                let bar = null;
                for (let n = grid.previousElementSibling; n; n = n.previousElementSibling) {
                    if (n.classList && n.classList.contains('agp-load-previous')) { bar = n; break; }
                }
                if (bar) {
                    this._prevBar = bar;
                    this._prevUrl = bar.dataset.prevUrl || null;
                    bar.addEventListener('click', (e) => {
                        const link = e.target.closest('.agp-load-previous__button');
                        if (!link || !this._prevUrl) return;
                        e.preventDefault();
                        this.loadPrevious();
                    });
                    // Auto-prepend while scrolling up (the upward mirror of the
                    // 800px bottom band), gated to browsers with native scroll
                    // anchoring: they absorb the prepend without any viewport
                    // movement, so the manual scrollBy below is just a ~0
                    // backstop. Safari (no overflow-anchor) can stutter when
                    // scrollBy lands mid-momentum-scroll, so it keeps the
                    // button-only flow. The button stays rendered either way —
                    // it is the affordance when the bar is VISIBLE (fresh
                    // ?page=N landing), where auto-loading would waste a fetch
                    // the visitor may not want.
                    this._autoPrev = typeof CSS !== 'undefined' && CSS.supports
                        && CSS.supports('overflow-anchor: auto');
                }

                // Remember which card the visitor leaves through. The click
                // also pins ?page to the CARD's page (the viewport-dominant
                // page can lag one behind at a boundary, and the return page
                // must actually contain this card).
                grid.addEventListener('click', (e) => {
                    // Modified clicks (new tab/window) keep the visitor on
                    // the list — leaving an anchor behind would teleport a
                    // later same-tab reload to a card they never left from.
                    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
                    const link = e.target.closest('a[href*="/products/"]');
                    if (!link || !grid.contains(link)) return;
                    const cell = link.closest('[data-agp-page]');
                    const page = cell ? parseInt(cell.dataset.agpPage, 10) : NaN;
                    if (!isNaN(page)) this._setPageParam(page);
                    try {
                        sessionStorage.setItem('agp:return', JSON.stringify({
                            key: this._trackKey(),
                            path: link.pathname,
                            t: Date.now()
                        }));
                    } catch (err) { /* storage blocked — degrade to page-top landing */ }
                });

                // rAF-throttled dominant-page tracker; also drives the upward
                // auto-prepend. The direction check matters twice over: no
                // surprise fetch on a fresh ?page=N landing (no upward scroll
                // has happened yet), and the compensation scrollBy after a
                // prepend reads as a DOWNWARD move, so one prepend can't
                // cascade into the next until the visitor actually scrolls
                // up again.
                this._trackScheduled = false;
                this._lastTrackY = window.scrollY;
                this._onTrackScroll = () => {
                    if (this._trackScheduled) return;
                    this._trackScheduled = true;
                    requestAnimationFrame(() => {
                        this._trackScheduled = false;
                        const y = window.scrollY;
                        const scrolledUp = y < this._lastTrackY;
                        this._lastTrackY = y;
                        this._syncPageParam();
                        if (scrolledUp) this._maybeAutoPrev();
                    });
                };
                window.addEventListener('scroll', this._onTrackScroll, { passive: true });

                this._restoreReturnAnchor(grid);
            }

            /**
             * Identity of this grid view for the return anchor: path plus
             * every query param EXCEPT page (filters change the result set,
             * page is exactly what the anchor is allowed to differ on).
             */
            _trackKey() {
                const params = new URLSearchParams(location.search);
                params.delete('page');
                const qs = params.toString();
                return location.pathname + (qs ? '?' + qs : '');
            }

            _pageFromUrl(u) {
                try {
                    return parseInt(new URL(u, location.href).searchParams.get('page'), 10) || 1;
                } catch (err) {
                    return null;
                }
            }

            _setPageParam(page) {
                if (page === this._reportedPage) return;
                this._reportedPage = page;
                try {
                    const url = new URL(location.href);
                    if (page > 1) url.searchParams.set('page', String(page));
                    else url.searchParams.delete('page');
                    history.replaceState(history.state, '', url);
                } catch (err) { /* sandboxed/rate-limited history — tracking is best-effort */ }
            }

            /**
             * Dominant page = the last loaded page whose first card starts
             * above the viewport midline. Only fires replaceState on change,
             * so scrolling inside one page costs zero history calls.
             */
            _syncPageParam() {
                if (!this._pageBounds || !this._pageBounds.length) return;
                const mid = (window.innerHeight || document.documentElement.clientHeight) / 2;
                let page = this._pageBounds[0].page;
                this._pageBounds.forEach((b) => {
                    if (b.el.isConnected && b.el.getBoundingClientRect().top <= mid) page = b.page;
                });
                this._setPageParam(page);
            }

            /**
             * Put a returning visitor back on the card they left through.
             * Only used on bfcache MISSES (a hit restores DOM + scroll by
             * itself): the browser's own restore targets a pixel offset from
             * the long pre-navigation page and would clamp to the footer of
             * this short one, so when the anchor matches we take restoration
             * over and center the card instead.
             */
            _restoreReturnAnchor(grid) {
                let entry = null;
                try { entry = JSON.parse(sessionStorage.getItem('agp:return')); } catch (err) { }
                if (!entry || entry.key !== this._trackKey()) return;
                if (Date.now() - (entry.t || 0) > 30 * 60 * 1000) return;
                let cell = null;
                const links = grid.querySelectorAll('a[href*="/products/"]');
                for (let i = 0; i < links.length; i++) {
                    if (links[i].pathname === entry.path) {
                        cell = links[i].closest('[data-agp-page]');
                        break;
                    }
                }
                if (!cell) return;
                try { sessionStorage.removeItem('agp:return'); } catch (err) { }
                if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
                const apply = () => cell.scrollIntoView({ block: 'center', behavior: 'instant' });
                apply();
                const appliedY = window.scrollY;
                // The first apply can run before the document below the grid
                // has its full height (deferred script, footer still coming)
                // and clamp short — re-center at load unless the visitor has
                // already scrolled away.
                window.addEventListener('load', () => {
                    if (Math.abs(window.scrollY - appliedY) < 2) apply();
                }, { once: true });
            }

            /**
             * Upward trigger band — the mirror of _autoTrigger's 800px bottom
             * band: while the visitor scrolls up, prepend the previous page
             * once the bar sits within 800px ABOVE the viewport (bottom <= 0
             * keeps it out of the visible-bar case, where the button is the
             * deliberate affordance). Only wired on scroll-anchoring browsers
             * (_autoPrev) — see _initPageTracking.
             */
            _maybeAutoPrev() {
                if (!this._autoPrev || !this._prevUrl || this._prevLoading || !this._prevBar) return;
                const bottom = this._prevBar.getBoundingClientRect().bottom;
                if (bottom <= 0 && bottom > -800) this.loadPrevious();
            }

            /**
             * Collapse the bar OUTSIDE a prepend batch (error path, empty
             * fetch): measured and compensated on its own, so the grid does
             * not jump up by the bar's height while the visitor looks at it.
             */
            _collapsePrevBar() {
                if (!this._prevBar || this._prevBar.classList.contains('agp-load-previous--done')) return;
                const ref = this._grid ? this._grid.firstElementChild : null;
                const before = ref ? ref.getBoundingClientRect().top : 0;
                this._prevBar.classList.add('agp-load-previous--done');
                if (ref) {
                    const delta = ref.getBoundingClientRect().top - before;
                    if (delta) window.scrollBy(0, delta);
                }
            }

            /**
             * Network-warm and decode the first-slide images of up to `count`
             * incoming cells BEFORE they are inserted, so cards land painted
             * instead of visibly loading into their boxes. Off-DOM Image()
             * clones with the same sizes/srcset/src hit the exact URL the
             * real <img> will request (shared HTTP cache), and decode()
             * resolves once pixels are ready. Capped at `capMs` so a slow
             * network degrades to the old swap-then-load behavior — the
             * skeletons/LQIP keep reserving the space either way.
             */
            _warmIncoming(cells, count, capMs, selector) {
                // Default: the HQ first-slide images. Callers pass the LQIP
                // selector to wait on the ~1KB thumbs instead, or capMs 0 to
                // fire the fetches without waiting at all.
                selector = selector || '.agp-card--first-slide img[src]:not(.agp-slide-lqip)';
                const desktop = window.matchMedia('(min-width: 1000px)').matches;
                const warmers = [];
                for (let i = 0; i < cells.length && warmers.length < count; i++) {
                    const cell = cells[i];
                    if (!cell.querySelectorAll) continue;
                    const imgs = cell.querySelectorAll(selector);
                    for (let j = 0; j < imgs.length && warmers.length < count; j++) {
                        const img = imgs[j];
                        if (img.closest('.agp-hidden')) continue;
                        if (img.closest(desktop ? '.agp-lg-hidden' : '.agp-sm-hidden')) continue;
                        const im = new Image();
                        // sizes before srcset before src — selection happens
                        // when src(set) lands, with whatever sizes is set.
                        const sizesAttr = img.getAttribute('sizes');
                        const srcsetAttr = img.getAttribute('srcset');
                        if (sizesAttr) im.sizes = sizesAttr;
                        if (srcsetAttr) im.srcset = srcsetAttr;
                        im.src = img.getAttribute('src');
                        warmers.push(im.decode().catch(() => { /* decode failure = fetch still warmed */ }));
                    }
                }
                if (!warmers.length) return Promise.resolve();
                return Promise.race([
                    Promise.all(warmers),
                    new Promise((resolve) => setTimeout(resolve, capMs))
                ]);
            }

            /**
             * Fetch the previous paginate page and prepend it in one batch —
             * via the button (user-initiated, CLS-exempt) or the upward band
             * (_maybeAutoPrev). Either way the insertion is scroll-compensated
             * so what the visitor is looking at does not visibly move: measure
             * a pre-insert reference card, mutate, re-measure, scrollBy the
             * delta in the same frame. Native scroll anchoring usually absorbs
             * it first (delta ~0); scroll-compensated moves are not counted as
             * layout shifts. When page 1 arrives the bar COLLAPSES inside that
             * same measured batch — display:none, its height folded into the
             * compensation — because a visibility-only hide left a permanent
             * bar-sized gap between the collection title and the grid.
             */
            async loadPrevious() {
                if (!this._prevUrl || this._prevLoading) return;
                const grid = this._grid || document.querySelector(this.gridSelector);
                if (!grid) return;
                this._prevLoading = true;
                if (this._prevBar) this._prevBar.classList.add('agp-load-previous--loading');
                const prevUrl = this._prevUrl;
                const fetchedPage = this._pageFromUrl(prevUrl);

                try {
                    const response = await fetch(prevUrl);
                    if (!response.ok) throw new Error(response.status);
                    const html = await response.text();
                    const doc = new DOMParser().parseFromString(html, 'text/html');
                    const newGrid = doc.querySelector(this.gridSelector);
                    const items = newGrid ? [...newGrid.children] : [];

                    // Resolve the onward chain BEFORE mutating the DOM: when
                    // the fetched page is page 1 (it renders no bar of its
                    // own), our bar must collapse inside the measured batch
                    // below so the compensation folds its height in with the
                    // prepended cards.
                    const nextBar = doc.querySelector('.agp-load-previous');
                    const nextPrevUrl = nextBar ? (nextBar.dataset.prevUrl || null) : null;

                    // Blur-first prepend: for a prepend it is the fetched
                    // page's LAST rows that sit nearest the viewport, so warm
                    // from the end — HQ fired without waiting, LQIP thumbs
                    // awaited. Must finish BEFORE the measured mutate/
                    // compensate block below — an await inside it would break
                    // the same-frame scroll compensation.
                    if (items.length) {
                        const nearestFirst = [...items].reverse();
                        this._warmIncoming(nearestFirst, 8, 0);
                        await this._warmIncoming(nearestFirst, 14, 250, '.agp-card--first-slide img.agp-slide-lqip');
                    }

                    if (items.length) {
                        if (fetchedPage != null) {
                            items.forEach((item) => {
                                item.dataset.agpPage = String(fetchedPage);
                            });
                            this._pageBounds.unshift({ page: fetchedPage, el: items[0] });
                        }
                        const ref = grid.firstElementChild;
                        const before = ref ? ref.getBoundingClientRect().top : 0;
                        grid.prepend(...items);
                        // Inside the measured batch: bucket info heights can
                        // change the prepended cells' placeholder heights, and
                        // the compensation below must fold that in.
                        agpAssignInfoH(grid, items);
                        if (!nextPrevUrl && this._prevBar) {
                            this._prevBar.classList.add('agp-load-previous--done');
                        }
                        if (ref) {
                            const delta = ref.getBoundingClientRect().top - before;
                            if (delta) window.scrollBy(0, delta);
                        }
                    } else if (!nextPrevUrl && this._prevBar) {
                        this._collapsePrevBar();
                    }

                    this._prevUrl = nextPrevUrl;
                    this._syncPageParam();
                } catch (e) {
                    console.error('[agp-infinite-scroll] Failed to load previous:', e);
                    this._prevUrl = null;
                    this._collapsePrevBar();
                } finally {
                    this._prevLoading = false;
                    if (this._prevBar) this._prevBar.classList.remove('agp-load-previous--loading');
                }
            }

            /**
             * Reserve the incoming page's grid space with skeleton cells (same
             * flex sizing as card cells via --cols-d/--cols-m, same vertical
             * structure via .agp-skeleton__media/__info). Returns the cells so
             * loadMore() can swap them for the real cards.
             *
             * The reserve is only ~net-zero if each skeleton gets the SAME
             * column count its real card will have. Column counts are
             * phase-based on the card's absolute index (total_idx in
             * agp-products-grid.liquid) and the live templates use non-uniform
             * modes (alternating desktop 4/3, two-two-one mobile), so the
             * per-index cycle is replicated here from the sentinel's
             * data-mode/fr/cols attributes, starting at _nextOffset (the
             * server-emitted total_idx of the next page's first item).
             * data-total-items caps the last page so the reserve never
             * overshoots the collection's end.
             *
             * Known, accepted inaccuracies (all shift-neutral on the live
             * templates): (a) search pages whose results mix in articles/
             * pages — the server phases total_idx over ALL result types but
             * only products render, so the cycle has holes no client can
             * predict; (b) 'first-row' mode combined with end-with-full-line,
             * where the server trims the LAST row of every page to the
             * remainder count — skeletons stay uniform c_d/c_m there.
             */
            _reserve(grid) {
                const offset = this._nextOffset;
                const total = parseInt(this.dataset.totalItems, 10);
                const remaining = isNaN(total) ? this._pageSize : total - offset;
                const count = Math.max(0, Math.min(this._pageSize, remaining));
                const cD = parseInt(this.colsD, 10) || 3;
                const cM = parseInt(this.colsM, 10) || 2;
                const frD = parseInt(this.dataset.frD, 10) || 0;
                const frM = parseInt(this.dataset.frM, 10) || 0;
                const modeD = this.dataset.modeD;
                const modeM = this.dataset.modeM;

                const cells = [];
                for (let i = 0; i < count; i++) {
                    const idx = offset + i;
                    // Desktop cycle (mirrors agp-products-grid.liquid):
                    // 'alternating' cycles fr_d -> c_d across pages;
                    // 'first-row' pages beyond 1 are uniform c_d (except the
                    // end-with-full-line last-row trim — see accepted
                    // inaccuracies above).
                    let colsD = cD;
                    if (modeD === 'alternating' && frD > 0) {
                        colsD = (idx % (frD + cD)) < frD ? frD : cD;
                    }
                    // Mobile cycle: 'two-two-one' repeats 2,2,2,2,1;
                    // 'alternating' cycles fr_m -> c_m; else uniform c_m.
                    let colsM = cM;
                    if (modeM === 'two-two-one') {
                        colsM = (idx % 5) === 4 ? 1 : 2;
                    } else if (modeM === 'alternating' && frM > 0) {
                        colsM = (idx % (frM + cM)) < frM ? frM : cM;
                    }
                    const cell = document.createElement('div');
                    cell.className = 'agp-skeleton-cell';
                    cell.style.setProperty('--cols-d', String(colsD));
                    cell.style.setProperty('--cols-m', String(colsM));
                    cell.innerHTML = '<div class="agp-skeleton__media"></div><div class="agp-skeleton__info"></div>';
                    cells.push(cell);
                    grid.appendChild(cell);
                }
                // Per-bucket info heights so the reserve matches what the
                // real cards will measure (agpSyncCardInfoHeight).
                agpAssignInfoH(grid, cells);
                return cells;
            }

            disconnectedCallback() {
                if (this.observer) this.observer.disconnect();
                if (this._onScroll) window.removeEventListener('scroll', this._onScroll);
                if (this._onTrackScroll) window.removeEventListener('scroll', this._onTrackScroll);
            }

            async loadMore() {
                if (!this.nextUrl || this.loading) return;
                this.loading = true;
                this.classList.add('agp-infinite-scroll--loading');

                // Reserve the space BEFORE fetching (see connectedCallback
                // comment): at auto-trigger time the grid bottom is below the
                // viewport so this grows nothing visible, and in manual mode
                // it lands synchronously inside the click's input window.
                const grid = document.querySelector(this.gridSelector);
                const skeletons = grid ? this._reserve(grid) : [];
                // Captured before the chain advances: page number for the
                // scroll-progress stamps on the appended cards.
                const fetchUrl = this.nextUrl;

                try {
                    const response = await fetch(fetchUrl);
                    if (!response.ok) throw new Error(response.status);
                    const html = await response.text();
                    const doc = new DOMParser().parseFromString(html, 'text/html');

                    // Extract new product cards from the fetched page
                    const newGrid = doc.querySelector(this.gridSelector);
                    // Check if there's another page
                    const nextEl = doc.querySelector('agp-infinite-scroll');
                    const isLastPage = !nextEl || !nextEl.dataset.nextUrl;

                    if (newGrid && grid) {
                        const items = [...newGrid.children];
                        // Blur-first swap: start the HQ first-slide fetches
                        // now WITHOUT waiting (they finish into already-
                        // visible cards as a blur→sharp upgrade) and only
                        // await the ~1KB LQIP thumbs. The grey-skeleton
                        // window collapses to roughly the HTML fetch, and
                        // cards land as soft-focus photos instead of popping
                        // sharp out of grey boxes — or grey boxes waiting
                        // 600ms for full decodes, as the first version did.
                        this._warmIncoming(items, 8, 0);
                        await this._warmIncoming(items, 14, 250, '.agp-card--first-slide img.agp-slide-lqip');
                        // Swap skeletons for the real cards in one synchronous
                        // batch — a single layout pass, so the handover is one
                        // ~net-zero height change, not an append shift.
                        skeletons.forEach(cell => cell.remove());
                        items.forEach(item => {
                            // The server already renders the correct
                            // --cols-d / --cols-m on every card based on the
                            // section's layout mode (first-row or alternating)
                            // and paginate.current_offset, so we MUST NOT
                            // override them here — doing so would force every
                            // appended card to use c_d/c_m and destroy the
                            // per-row layout (last-row trim, alternation, etc).
                            grid.appendChild(item);
                        });
                        agpAssignInfoH(grid, items);
                        if (items.length) this._pageSize = items.length;
                        if (this._trackPage && items.length) {
                            const fetchedPage = this._pageFromUrl(fetchUrl);
                            if (fetchedPage != null) {
                                items.forEach((item) => {
                                    item.dataset.agpPage = String(fetchedPage);
                                });
                                this._pageBounds.push({ page: fetchedPage, el: items[0] });
                            }
                        }
                    } else {
                        skeletons.forEach(cell => cell.remove());
                    }

                    if (!isLastPage) {
                        this.nextUrl = nextEl.dataset.nextUrl;
                        // Re-seed the column phase from the fetched page's own
                        // sentinel — exact on every surface (the appended item
                        // count only matches total_idx advancement on pure-
                        // product paginates).
                        const nextOffset = parseInt(nextEl.dataset.nextOffset, 10);
                        this._nextOffset = isNaN(nextOffset)
                            ? this._nextOffset + this._pageSize
                            : nextOffset;
                    } else {
                        this.nextUrl = null;
                        this.classList.add('agp-infinite-scroll--done');
                        if (this.observer) this.observer.disconnect();
                    }
                } catch (e) {
                    console.error('[agp-infinite-scroll] Failed to load:', e);
                    // On error, drop the reserved space and stop trying
                    skeletons.forEach(cell => cell.remove());
                    this.nextUrl = null;
                    this.classList.add('agp-infinite-scroll--done');
                    if (this.observer) this.observer.disconnect();
                } finally {
                    this.loading = false;
                    this.classList.remove('agp-infinite-scroll--loading');
                    // Re-evaluate the band after the swap settles. If the user
                    // caught up to the sentinel while the fetch was in flight,
                    // every trigger no-op'd on this.loading and — the swap
                    // being net-zero — the IO never re-fires (no intersection
                    // change) and no scroll event is pending: auto-loading
                    // would silently dead-end with the button still hidden.
                    // This either queues the next page (sentinel back in the
                    // band, below the viewport) or reveals the button
                    // (sentinel visible).
                    if (this.nextUrl && !this.manual) {
                        this._autoTrigger(this.getBoundingClientRect().top);
                    }
                }
            }
        });
    }

    /**
     * AGP grid placeholder calibration.
     *
     * agp-products-grid.liquid sizes content-visibility placeholders and the
     * infinite-scroll skeleton cells as `cell-width * 5/4 + --agp-card-info-h`
     * (media box + info block). The media term is exact (cards use
     * aspect-ratio 4/5); the info block height depends on typography, so it
     * is measured — not hardcoded — and re-measured on resize/fonts because
     * line wrapping changes it.
     *
     * PER COLUMN-COUNT BUCKET, not a single grid-wide value: cards at
     * different widths wrap their titles differently (a 4-col desktop card is
     * ~100px narrower than a 3-col one), so one measured height left a
     * ±10-25px placeholder error on the other bucket — the residual shift
     * when a cell first renders at the sentinel with the footer in view.
     * Per bucket we keep the MAX of up to 12 rendered samples: flex rows
     * stretch every cell to the row's tallest card, so max approximates the
     * real row height better than any single sample. Values land as an
     * inline --agp-card-info-h on EVERY cell (already-rendered cells ignore
     * it — contain-intrinsic's `auto` remembers their true height), on a
     * grid-level average as fallback, and on grid.__agpInfoH for skeletons
     * and appended/prepended cells (agpAssignInfoH).
     */
    function agpSyncCardInfoHeight() {
        const desktop = window.matchMedia('(min-width: 1000px)').matches;
        const bucketOf = (cell) => cell.style
            ? (desktop ? cell.style.getPropertyValue('--cols-d') : cell.style.getPropertyValue('--cols-m')).trim()
            : '';
        document.querySelectorAll('.agp-collection__grid').forEach((grid) => {
            const cells = [...grid.children];
            const heights = {};
            const sampled = {};
            for (const cell of cells) {
                const b = bucketOf(cell);
                if (!b || (sampled[b] || 0) >= 12) continue;
                // Only trust cells that are actually laid out: a never-
                // rendered content-visibility-skipped cell reports 0x0
                // descendants. The media box is the discriminator — it is
                // never legitimately 0 on a rendered card, while the info
                // block CAN be (media-only cards: 0 is a valid measurement).
                const media = cell.querySelector('.agp-card-carousel, .product-card__figure');
                const mediaH = media ? media.getBoundingClientRect().height : 0;
                if (mediaH <= 0) continue;
                sampled[b] = (sampled[b] || 0) + 1;
                // Everything that is NOT the media box: cell minus media.
                // This captures the info block AND the card's internal
                // spacing between figure and info — measuring the info box
                // alone missed ~7px/row of that spacing, which surfaced as a
                // +27px/page jump at bottom-park swaps. Flex rows stretch
                // cells to the row height, so cell−media on any sampled cell
                // reads as rowHeight−media — exactly the placeholder term.
                // (Media-only cards fall out naturally: cell≈media → ~0.)
                const h = Math.max(0, Math.round(cell.getBoundingClientRect().height - mediaH));
                heights[b] = Math.max(heights[b] !== undefined ? heights[b] : 0, h);
            }
            const buckets = Object.keys(heights);
            if (!buckets.length) return;
            const fallback = Math.round(buckets.reduce((a, b) => a + heights[b], 0) / buckets.length);
            // All reads done — writes only from here (no layout thrash).
            grid.style.setProperty('--agp-card-info-h', fallback + 'px');
            grid.__agpInfoH = heights;
            for (const cell of cells) {
                const h = heights[bucketOf(cell)];
                if (h !== undefined) cell.style.setProperty('--agp-card-info-h', h + 'px');
            }
        });
    }

    /**
     * Assign the measured per-bucket info heights to freshly created cells
     * (skeletons, infinite-scroll appends, Load-previous prepends) so their
     * placeholders are right from the first frame. Synchronous style writes
     * only — safe inside loadPrevious's measured/compensated batch.
     */
    function agpAssignInfoH(grid, cells) {
        const map = grid && grid.__agpInfoH;
        if (!map) return;
        const desktop = window.matchMedia('(min-width: 1000px)').matches;
        cells.forEach((cell) => {
            if (!cell.style) return;
            const b = (desktop ? cell.style.getPropertyValue('--cols-d') : cell.style.getPropertyValue('--cols-m')).trim();
            if (b in map) cell.style.setProperty('--agp-card-info-h', map[b] + 'px');
        });
    }
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', agpSyncCardInfoHeight);
    } else {
        agpSyncCardInfoHeight();
    }

    /**
     * Card placeholder shimmer stop.
     *
     * The shimmer/pulse overlay lives on ::before under the media (z-index 1
     * vs 2), so once the image loads it is covered — but covered is not
     * stopped: the animation kept running on every slide forever. Stamp the
     * slide when its media finishes loading; CSS then removes the pseudo
     * (.agp-slide--loaded rule in agp-product-card.css).
     *
     * Capture-phase listeners on document because load/loadeddata don't
     * bubble — capture still visits the target — and a single pair covers
     * every current AND future slide (infinite-scroll appends, Load-previous
     * prepends, quick-buy). The one-time sweep catches images that completed
     * before this module ran (module scripts execute after parsing, so
     * cached above-the-fold images can already be done).
     */
    function agpMarkSlideLoaded(el) {
        // Card slides and the PDP gallery share the stamp: both hide their
        // blur-up preview (and the card shimmer) once the real media is in.
        const slide = el.closest('.agp-card-carousel__slide, .product-gallery__media');
        if (slide) slide.classList.add('agp-slide--loaded');
    }
    document.addEventListener('load', (e) => {
        // The LQIP thumb loading must NOT stamp the slide as loaded — it IS
        // the placeholder (stamping would hide it and stop the shimmer).
        if (e.target && e.target.tagName === 'IMG' && !e.target.classList.contains('agp-slide-lqip')) agpMarkSlideLoaded(e.target);
    }, true);
    document.addEventListener('loadeddata', (e) => {
        if (e.target && e.target.tagName === 'VIDEO') agpMarkSlideLoaded(e.target);
    }, true);
    document.querySelectorAll('.agp-card-carousel__slide img:not(.agp-slide-lqip), .product-gallery__media img:not(.agp-slide-lqip)').forEach((img) => {
        if (img.complete && img.naturalWidth > 0) agpMarkSlideLoaded(img);
    });

    /**
     * Ahead-of-scroll image warm-up.
     *
     * Native loading=lazy starts fetching ~1250px ahead in Chrome, which a
     * fast scroller outruns — the card arrives before its image. This band
     * flips each card's FIRST slide to eager once the cell is within 2500px
     * of the viewport (both directions: downward scroll and the Load-previous
     * walk back up), roughly doubling the head start. First slides only —
     * the old regression eagerly preloaded carousel NEIGHBOR slides for every
     * card within 1500px and blew the back/forward-cache network budget; one
     * near-viewport image per card is a page's worth of images in flight at
     * worst. Skips the rendition for the other breakpoint (device-hidden
     * spans) and skeleton cells (no <img>). MutationObserver picks up
     * infinite-scroll appends and Load-previous prepends.
     */
    (function agpImageWarmup() {
        if (!('IntersectionObserver' in window) || !('MutationObserver' in window)) return;
        const isDesktop = () => window.matchMedia('(min-width: 1000px)').matches;
        const warmCell = (cell) => {
            const desktop = isDesktop();
            cell.querySelectorAll('.agp-card--first-slide img[loading="lazy"]').forEach((img) => {
                if (img.closest('.agp-hidden')) return;
                if (img.closest(desktop ? '.agp-lg-hidden' : '.agp-sm-hidden')) return;
                img.loading = 'eager';
            });
        };
        const io = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (!entry.isIntersecting) return;
                io.unobserve(entry.target);
                warmCell(entry.target);
            });
        }, { rootMargin: '2500px 0px' });
        const observeCell = (cell) => {
            if (cell.nodeType === 1 && cell.querySelector && cell.querySelector('img')) io.observe(cell);
        };
        document.querySelectorAll('.agp-collection__grid').forEach((grid) => {
            [...grid.children].forEach(observeCell);
            new MutationObserver((muts) => {
                muts.forEach((m) => m.addedNodes.forEach(observeCell));
            }).observe(grid, { childList: true });
        });
    })();
    // Font swap and viewport changes both re-wrap the title/price lines.
    window.addEventListener('load', agpSyncCardInfoHeight);
    let __agpInfoHTimer;
    window.addEventListener('resize', () => {
        clearTimeout(__agpInfoHTimer);
        __agpInfoHTimer = setTimeout(agpSyncCardInfoHeight, 250);
    }, { passive: true });

    /**
     * AGP Localization Selector
     *
     * Searchable country/language selector for the footer. Open/close, the mobile
     * full-width bottom sheet and the overlay are handled by the theme's <x-popover>
     * (.popover); the native {% form 'localization' %} submit buttons drive the
     * actual selection. This element ONLY adds client-side filtering on top
     * (no-JS fallback = full list with the detected option pinned on top).
     *
     * Markup contract (snippets/agp-localization-selector.liquid):
     * - .agp-loc__search (optional text input, inside the popover)
     * - .agp-loc__option [data-search] (the .agp-loc__option--pinned option is
     *   exempt from filtering)
     * - .agp-loc__empty (no-results message)
     */
    class AgpLocalizationSelector extends HTMLElement {
        connectedCallback() {
            this.search = this.querySelector('.agp-loc__search');
            this.options = Array.from(this.querySelectorAll('.agp-loc__option'));
            this.empty = this.querySelector('.agp-loc__empty');
            this.list = this.querySelector('.agp-loc__list');
            if (!this.search) return;
            this.onSearch = this.handleSearch.bind(this);
            this.search.addEventListener('input', this.onSearch);
        }

        handleSearch() {
            // Lock the list at its full-list height on first use so filtering
            // never resizes the popover (offsetHeight is read before any option
            // is hidden). Node references survive x-popover's reparent to body.
            if (this.list && !this.list.style.minHeight) {
                this.list.style.minHeight = this.list.offsetHeight + 'px';
            }
            const query = this.search.value.trim().toLowerCase();
            let visible = 0;
            this.options.forEach((option) => {
                if (option.classList.contains('agp-loc__option--pinned')) {
                    visible++;
                    return; // pinned/detected option is exempt from filtering
                }
                const haystack = option.getAttribute('data-search') || '';
                const match = query === '' || haystack.indexOf(query) !== -1;
                option.hidden = !match;
                if (match) visible++;
            });
            if (this.empty) this.empty.hidden = visible > 0;
        }
    }

    if (!customElements.get('agp-localization-selector')) {
        customElements.define('agp-localization-selector', AgpLocalizationSelector);
    }

    /**
     * AGP Footer Accordion (one per menu column)
     *
     * Mirrors the AGP Accordion blocks' experience (icon animation + panel
     * max-height transition come from .agp-accordion CSS in agapee.scss), but as a
     * separate element so close-others stays scoped to the FOOTER (the real
     * <agp-accordion> closes every accordion on the page, which would collapse
     * product-page accordions).
     * - Desktop (>=1000px): always open + non-interactive (CSS forces the panel open).
     * - Mobile (<1000px): collapsible. Supports data-open-mobile (open by default),
     *   data-close-others, data-scroll-on-open, data-scroll-subtract-header.
     *
     * Markup contract: .agp-accordion__title (button) / .agp-accordion__panel /
     * .agp-accordion__content (same classes as the AGP accordion block).
     */
    class AgpFooterAccordion extends HTMLElement {
        connectedCallback() {
            this.toggleBtn = this.querySelector('.agp-accordion__title');
            this.panel = this.querySelector('.agp-accordion__panel');
            this.content = this.querySelector('.agp-accordion__content');
            if (!this.toggleBtn || !this.panel) return;
            this.openByDefaultMobile = this.hasAttribute('data-open-mobile');
            this.closeOthers = this.hasAttribute('data-close-others');
            this.scrollOnOpen = this.hasAttribute('data-scroll-on-open');
            this.subtractHeader = this.hasAttribute('data-scroll-subtract-header');
            this.mql = window.matchMedia('(min-width: 1000px)');
            this.onToggle = this.handleToggle.bind(this);
            this.onViewport = this.applyViewport.bind(this);
            this.toggleBtn.addEventListener('click', this.onToggle);
            this.mql.addEventListener('change', this.onViewport);
            this.applyViewport();
        }

        disconnectedCallback() {
            if (this.toggleBtn) this.toggleBtn.removeEventListener('click', this.onToggle);
            if (this.mql) this.mql.removeEventListener('change', this.onViewport);
        }

        get isOpen() {
            return this.classList.contains('is-open');
        }

        applyViewport() {
            if (this.mql.matches) {
                // Desktop: CSS forces the panel open; reflect the open state.
                this.classList.add('is-open');
                this.toggleBtn.setAttribute('aria-expanded', 'true');
                this.panel.setAttribute('aria-hidden', 'false');
                this.panel.style.maxHeight = '';
            } else {
                this.setOpen(this.openByDefaultMobile, false);
            }
        }

        setOpen(open, animate) {
            this.classList.toggle('is-open', open);
            this.toggleBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
            this.panel.setAttribute('aria-hidden', open ? 'false' : 'true');
            if (animate && !this.classList.contains('has-interacted')) {
                this.classList.add('has-interacted');
            }
            if (open) {
                const target = this.content || this.panel;
                this.panel.style.maxHeight = target.scrollHeight + 'px';
            } else {
                this.panel.style.maxHeight = '0';
            }
        }

        close() {
            if (!this.isOpen) return;
            this.setOpen(false, true);
        }

        handleToggle() {
            if (this.mql.matches) return; // non-interactive on desktop
            if (this.isOpen) {
                this.setOpen(false, true);
                return;
            }

            // Opening: optionally close other FOOTER accordions, tracking how much
            // collapses above us (to keep the title in view).
            let closingHeightAbove = 0;
            if (this.closeOthers) {
                document.querySelectorAll('agp-footer-accordion').forEach((acc) => {
                    if (acc === this || !acc.isOpen) return;
                    if (this.compareDocumentPosition(acc) & Node.DOCUMENT_POSITION_PRECEDING) {
                        const otherPanel = acc.querySelector('.agp-accordion__panel');
                        if (otherPanel) closingHeightAbove += otherPanel.scrollHeight;
                    }
                    acc.close();
                });
            }

            this.setOpen(true, true);

            // Reset scroll if the title would be pushed out of view once panels above collapse.
            if (this.scrollOnOpen && closingHeightAbove > 0) {
                let offset = 0;
                if (this.subtractHeader) {
                    const probe = document.createElement('div');
                    probe.style.cssText = 'position:absolute;top:0;left:0;height:var(--sticky-area-height,0px);pointer-events:none;visibility:hidden;';
                    document.body.appendChild(probe);
                    offset = probe.offsetHeight;
                    probe.remove();
                }
                const futureTop = this.getBoundingClientRect().top - closingHeightAbove;
                if (futureTop < offset) {
                    const scrollTarget = this.getBoundingClientRect().top + window.scrollY - closingHeightAbove - offset;
                    window.scrollTo({ top: scrollTarget, behavior: 'smooth' });
                }
            }
        }
    }

    if (!customElements.get('agp-footer-accordion')) {
        customElements.define('agp-footer-accordion', AgpFooterAccordion);
    }

    /**
     * Footer "See more" link limit. Delegated click handler (bound once) so it
     * survives Shopify section reloads without double-binding. Toggles the extra
     * links, swaps the label (data-more/data-less), rotates the caret, and — when
     * the column is an open mobile accordion — recalculates the panel height.
     */
    function initFooterSeeMore() {
        if (initFooterSeeMore._bound) return;
        initFooterSeeMore._bound = true;
        document.addEventListener('click', (event) => {
            const btn = event.target.closest('.agp-footer__see-more');
            if (!btn) return;
            const list = btn.parentElement.querySelector('.agp-footer__links');
            if (!list) return;
            const expanded = list.classList.toggle('is-expanded');
            btn.setAttribute('aria-expanded', expanded ? 'true' : 'false');
            const textEl = btn.querySelector('.agp-footer__see-more-text');
            if (textEl) {
                const next = expanded ? textEl.dataset.less : textEl.dataset.more;
                if (next) textEl.textContent = next;
            }
            // If we're inside an open accordion panel, grow it to fit.
            const panel = btn.closest('.agp-accordion__panel');
            if (panel && panel.style.maxHeight && panel.style.maxHeight !== 'none' && panel.style.maxHeight !== '0px') {
                const content = panel.querySelector('.agp-accordion__content') || panel;
                panel.style.maxHeight = content.scrollHeight + 'px';
            }
        });
    }

    /**
     * Contact page actions. Delegated (bound once): the "Email" card scrolls to
     * the contact form, the "Chat" card opens the Gorgias chat widget.
     *
     * Scroll target resolves to #agp-contact-form when present, otherwise the
     * Gorgias embed itself ([data-gorgias-contact-form-uid]) — the latter is
     * always in the DOM regardless of the custom-liquid wrapper, so the scroll
     * survives the theme editor reverting the wrapper's id. The sticky-header
     * offset is handled by the theme's `scroll-padding-top` on <html>.
     */
    function initContactActions() {
        if (initContactActions._bound) return;
        initContactActions._bound = true;
        document.addEventListener('click', (event) => {
            const scrollBtn = event.target.closest('[data-contact-scroll]');
            if (scrollBtn) {
                const target = document.getElementById('agp-contact-form')
                    || document.querySelector('[data-gorgias-contact-form-uid]');
                if (target) {
                    event.preventDefault();
                    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
                return;
            }
            const chatBtn = event.target.closest('[data-contact-chat]');
            if (chatBtn) {
                event.preventDefault();
                if (window.GorgiasChat && typeof window.GorgiasChat.open === 'function') {
                    window.GorgiasChat.open();
                }
            }
        });
    }

    /**
     * Initialize all Agapee JS features
     */
    function init() {
        initOverlayHover();
        initParallax();
        initProductCardBadges();
        initFooterSeeMore();
        initContactActions();
    }

    // Run on DOMContentLoaded
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }

    // Re-initialize on Shopify section load (for theme editor)
    document.addEventListener('shopify:section:load', init);
})();

/* ============================================================================
 * AGP Fixed ATC (Product Page v2)
 * Keeps the sticky bar's own product form in sync with the selected variant,
 * reflects availability on the ATC button, and reserves page-bottom space so
 * the fixed bar never permanently covers content. See snippets/agp-fixed-atc.liquid.
 * ============================================================================ */
if (window.customElements && !customElements.get('agp-fixed-atc')) {
    customElements.define('agp-fixed-atc', class extends HTMLElement {
        connectedCallback() {
            this.input = this.querySelector('[data-agp-fatc-variant]');
            this.button = this.querySelector('[data-agp-fatc-button]');
            this.label = this.querySelector('[data-agp-fatc-label]');
            this.priceEl = this.querySelector('[data-agp-fatc-price]');
            this.express = this.querySelector('[data-agp-fatc-express]');
            this.mainFormId = this.dataset.mainForm;
            // Seed the availability from what Liquid rendered. Without this it stays undefined
            // until the first variant:change — which never fires on a single-variant product — and
            // `_variantAvailable !== false` then reads an unknown state as "in stock", re-showing
            // the express button on a sold-out product.
            this._variantAvailable = this.dataset.variantAvailable !== 'false';
            // "Hide the 1-click button when the variant is out of stock" (default on).
            this._hideExpressWhenUnavailable = this.dataset.expressHideUnavailable !== '0';
            this.notifyEnabled = this.dataset.notifyEnabled === 'true';
            this.notifyText = this.dataset.notifyText || 'Notify me when available';
            this.infoPrice = this.querySelector('[data-agp-fatc-info-price]');
            this.variantText = this.querySelector('[data-agp-fatc-variant-text]');
            this.variantLink = this.querySelector('[data-agp-fatc-variant-link]');
            this._onVariant = this._onVariant.bind(this);
            this._reserve = this._reserve.bind(this);
            this._syncFromMain = this._syncFromMain.bind(this);
            this._onButtonClick = this._onButtonClick.bind(this);
            this._onExpandScroll = this._onExpandScroll.bind(this);
            this._onVariantLink = this._onVariantLink.bind(this);
            this._fitSplit = this._fitSplit.bind(this);
            this._syncRow = this._syncRow.bind(this);
            // "Expanded at top of page": collapse the info row (and the top-only 1-click button)
            // as soon as the shopper scrolls past the threshold.
            if (this.classList.contains('agp-fixed-atc--expandable')) {
                this._threshold = parseInt(this.dataset.expandThreshold, 10);
                if (isNaN(this._threshold)) this._threshold = 40;
                window.addEventListener('scroll', this._onExpandScroll, { passive: true });
                this._onExpandScroll();
            }
            if (this.variantLink) this.variantLink.addEventListener('click', this._onVariantLink);
            // Optional: only keep the 1-click button when Shopify serves Apple Pay / Google Pay.
            if (this.express && window.agpAnyWalletHidden()) this._watchWallet();
            // Optional: stay out of the way while the product's own Add-to-cart button is on
            // screen, and slide back in once it scrolls away (or gets covered).
            if (this.dataset.hideWhenAtcVisible === '1') {
                this.mainAtc = document.querySelector('.product-info agp-buy-button')
                            || document.querySelector('.product-info [data-agp-buy-button]')
                            || document.querySelector('.product-info button[type="submit"]');
                if (this.mainAtc) {
                    this._onAtcWatch = this._onAtcWatch.bind(this);
                    window.addEventListener('scroll', this._onAtcWatch, { passive: true });
                    window.addEventListener('resize', this._onAtcWatch, { passive: true });
                    this._onAtcWatch();
                }
            }
            // Primary sync: follow the main variant picker live (needed by the express / Shop Pay button).
            document.addEventListener('variant:change', this._onVariant);
            // Fallback sync: mirror the main form's authoritative variant id on load and right before the
            // ATC submits — kills any tap-before-event race or a missed variant:change event.
            this._syncFromMain();
            if (this.button) this.button.addEventListener('click', this._onButtonClick);
            this.row = this.querySelector('.agp-fixed-atc__row');
            this._syncRow();
            if ('ResizeObserver' in window) {
                this._ro = new ResizeObserver(this._syncRow);
                this._ro.observe(this);
            }
            window.addEventListener('resize', this._syncRow);
            // Truncation guard: the wallet renders after us and its label is localized, so
            // re-measure whenever the express slot's contents change.
            if (this.express && this.row) {
                this._fitMo = new MutationObserver(this._syncRow);
                this._fitMo.observe(this.express, { childList: true, subtree: true });
            }
        }
        disconnectedCallback() {
            document.removeEventListener('variant:change', this._onVariant);
            window.removeEventListener('resize', this._syncRow);
            window.removeEventListener('scroll', this._onExpandScroll);
            if (this.button) this.button.removeEventListener('click', this._onButtonClick);
            if (this.variantLink) this.variantLink.removeEventListener('click', this._onVariantLink);
            if (this._expandRaf) { cancelAnimationFrame(this._expandRaf); this._expandRaf = 0; }
            if (this._onAtcWatch) {
                window.removeEventListener('scroll', this._onAtcWatch);
                window.removeEventListener('resize', this._onAtcWatch);
            }
            if (this._watchRaf) { cancelAnimationFrame(this._watchRaf); this._watchRaf = 0; }
            if (this._ro) this._ro.disconnect();
            if (this._fitMo) { this._fitMo.disconnect(); this._fitMo = null; }
            document.documentElement.style.removeProperty('--agp-fatc-space');
        }

        // Expanded only while the shopper is at the top of the page.
        _onExpandScroll() {
            if (this._expandRaf) return;
            var self = this;
            this._expandRaf = requestAnimationFrame(function () {
                self._expandRaf = 0;
                var y = window.scrollY || window.pageYOffset || 0;
                var was = self.classList.contains('agp-fixed-atc--expanded');
                var now = y <= self._threshold;
                self.classList.toggle('agp-fixed-atc--expanded', now);
                // Collapsing hides the top-only 1-click button and changes the bar's height, so
                // re-measure the row on a state flip; otherwise just the reserved space.
                if (was !== now && self.row) self._syncRow(); else self._reserve();
            });
        }

        // Hide the bar only while the shopper can ACTUALLY see the page's own Add-to-cart button.
        // A viewport-intersection test isn't enough: the product section can be pinned (scroll
        // reveal) so the button stays "in the viewport" while the recommendations sheet covers it
        // — hence the hit test. Our own bar is excluded, otherwise it would hide the button it is
        // measuring.
        _onAtcWatch() {
            if (this._watchRaf) return;
            var self = this;
            this._watchRaf = requestAnimationFrame(function () {
                self._watchRaf = 0;
                var el = self.mainAtc;
                if (!el) return;
                var r = el.getBoundingClientRect();
                var vh = window.innerHeight;
                var seen = r.height > 0 && r.bottom > 0 && r.top < vh;
                if (seen) {
                    var cx = Math.round(r.left + r.width / 2);
                    var cy = Math.round(Math.min(vh - 2, Math.max(2, r.top + r.height / 2)));
                    var hit = document.elementFromPoint(cx, cy);
                    var covered = true;
                    while (hit) {
                        if (hit === el || hit === self) { covered = false; break; }
                        hit = hit.parentElement;
                    }
                    if (covered) seen = false;
                }
                self.classList.toggle('agp-fixed-atc--hidden', seen);
                self._reserve(); // hidden reserves no bottom space
            });
        }

        // Tapping the variant chip scrolls to the variant picker in the product info column.
        _onVariantLink(e) {
            e.preventDefault();
            var picker = document.querySelector('.product-info .variant-picker')
                      || document.querySelector('.variant-picker');
            if (!picker) return;
            var reduce = !window.matchMedia('(prefers-reduced-motion: no-preference)').matches;
            // Offset by the sticky header so the picker isn't hidden underneath it.
            var probe = document.createElement('div');
            probe.style.cssText = 'position:absolute;top:0;height:var(--sticky-area-height,0px);visibility:hidden;pointer-events:none;';
            document.body.appendChild(probe);
            var offset = probe.offsetHeight;
            probe.remove();
            var y = picker.getBoundingClientRect().top + (window.scrollY || window.pageYOffset || 0) - offset - 16;
            window.scrollTo({ top: y < 0 ? 0 : y, behavior: reduce ? 'auto' : 'smooth' });
        }
        _syncFromMain() {
            // Copy the main product form's authoritative selected-variant id into our hidden input.
            if (!this.input || !this.mainFormId) return;
            var mainForm = document.getElementById(this.mainFormId);
            var mainId = mainForm && mainForm.querySelector('input[name="id"]');
            if (mainId && mainId.value) this.input.value = mainId.value;
        }
        _onVariant(event) {
            var v = event.detail && (event.detail.variant || event.detail);
            if (!v) return;
            if (this.input && v.id) this.input.value = v.id;
            var avail = v.available !== false;
            // Hide the express / 1-click button when the variant is out of stock (mirrors the
            // AGP Buy Button); dropping --split returns the ATC to a full-width bar.
            this._variantAvailable = avail;
            this._applyExpressVisibility();
            if (!this.button) return;
            var labelEl = this.label || this.button;
            if (!avail) {
                if (this.notifyEnabled) {
                    // Back-in-stock: turn the bar into a "Notify me when available" button.
                    // Kept enabled + type=button so it doesn't add to cart; the click is
                    // delegated to the main notify button (see _onButtonClick).
                    this.button.disabled = false;
                    this.button.type = 'button';
                    this.button.setAttribute('data-agp-fatc-notify', '');
                    labelEl.textContent = this.notifyText;
                } else {
                    this.button.disabled = true;
                    this.button.type = 'submit';
                    this.button.removeAttribute('data-agp-fatc-notify');
                    if (this.dataset.soldOutText) labelEl.textContent = this.dataset.soldOutText;
                }
            } else {
                this.button.disabled = false;
                this.button.type = 'submit';
                this.button.removeAttribute('data-agp-fatc-notify');
                if (this.dataset.atcText) labelEl.textContent = this.dataset.atcText;
            }
            // Price-in-button: refresh the amount and hide it while sold out.
            if (this.priceEl) {
                if (avail && typeof v.price === 'number') this.priceEl.textContent = ' — ' + this._formatMoney(v.price);
                this.priceEl.hidden = !avail;
            }
            // Expanded info row: keep the price and the selected-variant chip current.
            if (this.infoPrice && typeof v.price === 'number') this.infoPrice.textContent = this._formatMoney(v.price);
            if (this.variantText && v.title) this.variantText.textContent = v.title;
            // The ATC label just changed width (Add to cart / Sold out / Notify me / price).
            if (this.row) this._syncRow();
        }
        // Single place deciding whether the 1-click button shows: it must be in stock (unless the
        // merchant turned that guard off) AND the wallet Shopify served must not be one they hid.
        _applyExpressVisibility() {
            if (!this.express) return;
            var inStock = this._variantAvailable !== false || !this._hideExpressWhenUnavailable;
            var show = inStock && !this._walletBlocked;
            this.express.style.display = show ? '' : 'none';
            this.classList.toggle('agp-fixed-atc--split', show);
            if (this.row) this._syncRow();
        }

        // Which wallets may show here: the section's per-wallet hides (see agpWatchWallet /
        // agpWalletHidden at the top of this file).
        _watchWallet() {
            var self = this;
            window.agpWatchWallet(this.express, function (name) {
                self._walletBlocked = window.agpWalletHidden(name);
                self._applyExpressVisibility();
            });
        }

        _onButtonClick(e) {
            // In back-in-stock mode the bar is a notify button, not an add-to-cart.
            if (this.button && this.button.hasAttribute('data-agp-fatc-notify')) {
                if (e) e.preventDefault();
                this._triggerNotify();
                return;
            }
            // Normal add-to-cart: sync the hidden variant id from the main form before submit.
            this._syncFromMain();
        }
        _triggerNotify() {
            // Delegate to the AGP Buy Button's notify button — that's where the theme's
            // Klaviyo back-in-stock trigger is bound. Prefer one outside a quick-buy modal.
            var main = document.querySelector('.product-info [data-notification-button]')
                    || document.querySelector('[data-notification-button]');
            if (main) { main.click(); return; }
            // Fallback: dispatch the theme's back-in-stock event.
            document.dispatchEvent(new CustomEvent('back-in-stock:open', {
                detail: { variantId: this.input && this.input.value }
            }));
        }
        _formatMoney(cents) {
            try {
                var f = new Intl.NumberFormat((window.Shopify && Shopify.locale) || 'en', {
                    style: 'currency',
                    currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'EUR'
                });
                var out = f.format(cents / 100);
                if (this.dataset.currencyCodeEnabled === 'true') {
                    var code = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'EUR';
                    if (out.indexOf(code) === -1) out += ' ' + code;
                }
                return out;
            } catch (e) {
                return (cents / 100).toFixed(2);
            }
        }
        // Truncation guard for the bar's "Add to cart + 1-click" row — see window.agpFitSplitRow.
        // Only armed while the row really is split (in stock, wallet allowed, express visible).
        _fitSplit() {
            if (!window.agpFitSplitRow || !this.row || !this.express) return;
            window.agpFitSplitRow({
                host: this,
                row: this.row,
                atc: this.button,
                slot: this.express,
                minClass: 'agp-fixed-atc--express-min',
                stackClass: 'agp-fixed-atc--stacked-fallback',
                active: this.classList.contains('agp-fixed-atc--split'),
                autoWidth: this.dataset.autoWidth === '1',
                allowStacked: this.dataset.allowStacked === '1',
            });
        }

        // Re-measure the row, then the space it reserves (stacking changes the bar's height).
        _syncRow() {
            this._fitSplit();
            this._reserve();
        }

        _reserve() {
            // Hidden (main ATC on screen) reserves nothing, so no phantom gap at the page bottom.
            if (window.matchMedia('(max-width: 999px)').matches && !this.classList.contains('agp-fixed-atc--hidden')) {
                document.documentElement.style.setProperty('--agp-fatc-space', this.offsetHeight + 'px');
            } else {
                document.documentElement.style.removeProperty('--agp-fatc-space');
            }
        }
    });
}

/* ============================================================================
 * AGP Pay Later (Product Page v2)
 * Parses the block's JSON country_config, matches the shopper's country, and
 * renders an interest-free-payments line + the PayPal/Klarna logo. Template
 * placeholders {count} {amount} {logo}; the per-installment amount updates on
 * variant change. Hidden until a country match is found. See snippets/agp-pay-later.liquid.
 * ============================================================================ */
if (window.customElements && !customElements.get('agp-pay-later')) {
    customElements.define('agp-pay-later', class extends HTMLElement {
        connectedCallback() {
            this.textEl = this.querySelector('.agp-pay-later__text');
            this._onVariant = this._onVariant.bind(this);
            try {
                this.config = JSON.parse(this.dataset.config || '{}');
            } catch (e) {
                this.config = null;
            }
            this.entry = this._lookup(this.dataset.country);
            if (!this.entry || !this.textEl) return; // no match → stays hidden
            this.price = parseInt(this.dataset.price, 10) || 0;
            this._render();
            this.removeAttribute('hidden');
            document.addEventListener('variant:change', this._onVariant);
        }
        disconnectedCallback() {
            document.removeEventListener('variant:change', this._onVariant);
        }
        _lookup(country) {
            if (!this.config || !country) return null;
            country = country.toUpperCase();
            for (const key in this.config) {
                if (key.toUpperCase() === country) {
                    const e = this.config[key];
                    let provider, count;
                    if (Array.isArray(e)) { provider = e[0]; count = e[1]; }
                    else if (e && typeof e === 'object') { provider = e.provider; count = e.count; }
                    if (count) {
                        return { provider: String(provider || '').toLowerCase(), count: parseInt(count, 10) };
                    }
                }
            }
            return null;
        }
        _onVariant(event) {
            const vr = event.detail && (event.detail.variant || event.detail);
            if (vr && typeof vr.price === 'number') {
                this.price = vr.price;
                this._render();
            }
        }
        _formatMoney(cents) {
            try {
                const fmt = new Intl.NumberFormat((window.Shopify && Shopify.locale) || 'en', {
                    style: 'currency',
                    currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'EUR'
                });
                let out = fmt.format(cents / 100);
                if (this.dataset.currencyCodeEnabled === 'true') {
                    const code = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'EUR';
                    if (!out.includes(code)) out += ' ' + code;
                }
                return out;
            } catch (e) {
                return (cents / 100).toFixed(2);
            }
        }
        _logoHTML() {
            const src = this.querySelector('.agp-pay-later__logo-src[data-provider="' + this.entry.provider + '"]');
            if (!src) return '';
            // Per-provider vertical offset (baseline correction; e.g. PayPal's "y" descender).
            const style = 'transform: translateY(var(--agp-pl-offset-' + this.entry.provider + ', 0px));';
            return '<span class="agp-pay-later__logo" style="' + style + '">' + src.innerHTML + '</span>';
        }
        _render() {
            const count = this.entry.count;
            const amount = this._formatMoney(Math.round(this.price / count));
            let html = this.dataset.template || '{count} interest-free payments {logo}';
            html = html.split('{count}').join(count)
                       .split('{amount}').join(amount)
                       .split('{logo}').join(this._logoHTML());
            this.textEl.innerHTML = html;
        }
    });
}

/* ============================================================================
 * <agp-gallery-dots> — Product Page v2, step 5 (vertical-swipe gallery)
 * ----------------------------------------------------------------------------
 * Drives the right-side vertical dots for the opt-in mobile vertical gallery.
 * The theme's scroll-carousel/carousel-navigation track the active slide via
 * scrollLeft (horizontal only), so vertical mode needs its own axis-agnostic
 * controller: an IntersectionObserver (root = carousel) marks the most-visible
 * slide's dot aria-current, and tapping a dot scrolls that slide into view.
 *
 * Variant changes: the theme's <product-gallery> hides non-variant cells
 * (carousel:filter) and then tries a HORIZONTAL select() that no-ops under our
 * overflow-x:hidden. So we mirror the theme's carousel-navigation by re-syncing
 * the dots' [hidden] on carousel:filter, and (since select() can't move us) we
 * scroll the column to the variant's featured media ourselves on variant:change.
 * See snippets/agp-product_gallery.liquid.
 * ============================================================================ */
if (window.customElements && !customElements.get('agp-gallery-dots')) {
    customElements.define('agp-gallery-dots', class extends HTMLElement {
        connectedCallback() {
            this.carousel = document.getElementById(this.getAttribute('aria-controls'));
            if (!this.carousel) return;
            this.dots = Array.from(this.querySelectorAll('button'));
            this.slides = Array.from(this.carousel.querySelectorAll('.product-gallery__media'));
            if (!this.dots.length || !this.slides.length) return;

            // Tap a dot → bring its slide into the carousel viewport.
            this._onClick = this._onClick.bind(this);
            this.dots.forEach(function (btn) { btn.addEventListener('click', this._onClick); }, this);

            // Active dot follows whichever slide is most visible in the carousel.
            if ('IntersectionObserver' in window) {
                this._io = new IntersectionObserver(function (entries) {
                    var best = null;
                    entries.forEach(function (e) {
                        if (!best || e.intersectionRatio > best.intersectionRatio) best = e;
                    });
                    if (best && best.isIntersecting && best.intersectionRatio >= 0.5) {
                        this._setActive(this.slides.indexOf(best.target));
                    }
                }.bind(this), { root: this.carousel, threshold: [0.25, 0.5, 0.75, 1] });
                this.slides.forEach(function (s) { this._io.observe(s); }, this);
            }

            // Keep dots in sync with variant-image filtering + navigation.
            this._onFilter = this._onFilter.bind(this);
            this.carousel.addEventListener('carousel:filter', this._onFilter);
            this._onVariant = this._onVariant.bind(this);
            document.addEventListener('variant:change', this._onVariant);
        }
        disconnectedCallback() {
            if (this._io) this._io.disconnect();
            if (this._onClick && this.dots) {
                this.dots.forEach(function (btn) { btn.removeEventListener('click', this._onClick); }, this);
            }
            if (this._onFilter && this.carousel) this.carousel.removeEventListener('carousel:filter', this._onFilter);
            if (this._onVariant) document.removeEventListener('variant:change', this._onVariant);
        }
        _onClick(event) {
            var slide = this.slides[parseInt(event.currentTarget.dataset.index, 10)];
            if (slide) this._scrollToSlide(slide);
        }
        // Mirror the theme carousel-navigation: hide dots whose cells were filtered out.
        _onFilter(event) {
            var hidden = (event.detail && event.detail.filteredIndexes) || [];
            this.dots.forEach(function (dot, i) { dot.hidden = hidden.indexOf(i) !== -1; });
        }
        // The theme's horizontal select() can't move a vertical gallery, so scroll to
        // the variant's featured media ourselves (deferred until filtering settles).
        _onVariant(event) {
            var variant = event.detail && event.detail.variant;
            var mediaId = variant && variant.featured_media && variant.featured_media.id;
            if (mediaId == null) return;
            var self = this;
            requestAnimationFrame(function () {
                var target = self.carousel.querySelector('.product-gallery__media[data-media-id="' + mediaId + '"]');
                if (target && !target.hasAttribute('hidden')) self._scrollToSlide(target);
            });
        }
        _scrollToSlide(slide) {
            var delta = slide.getBoundingClientRect().top - this.carousel.getBoundingClientRect().top;
            this.carousel.scrollTo({ top: this.carousel.scrollTop + delta, behavior: 'smooth' });
            this._setActive(this.slides.indexOf(slide));
        }
        _setActive(idx) {
            if (idx < 0 || idx === this._active) return;
            this._active = idx;
            this.dots.forEach(function (d, i) {
                d.setAttribute('aria-current', i === idx ? 'true' : 'false');
            });
        }
    });
}

/* ============================================================================
 * AGP Gallery Grid (Product Page v2 — desktop 2-column grid, grid_2x)
 * Two desktop-only behaviours; both work by toggling classes that agapee.css
 * only acts on at >=1000px, so the mobile carousel / vertical-swipe is untouched:
 *   1. Full-width-last: when the number of VISIBLE media cells is odd, the last
 *      one is alone on its row → span it across both columns (.agp-grid-full).
 *   2. "See more" (only when data-limit is set): show the first N visible cells
 *      (.agp-grid-hidden on the rest) + a button that reveals them on click.
 * "Visible" excludes variant-filtered cells (the theme sets [hidden] on those),
 * so it recomputes on variant:change. See snippets/agp-product_gallery.liquid.
 * ============================================================================ */
if (window.customElements && !customElements.get('agp-gallery-grid')) {
    customElements.define('agp-gallery-grid', class extends HTMLElement {
        connectedCallback() {
            this.carousel = document.getElementById(this.getAttribute('aria-controls'));
            if (!this.carousel) return;
            var limit = parseInt(this.getAttribute('data-limit'), 10);
            this.limit = isNaN(limit) ? 0 : limit; // 0 = no see-more limit
            this.button = this.querySelector('[data-see-more]');
            this.expanded = false;
            this._apply = this._apply.bind(this);
            this._onMore = this._onMore.bind(this);
            this._onVariant = this._onVariant.bind(this);
            if (this.button) this.button.addEventListener('click', this._onMore);
            document.addEventListener('variant:change', this._onVariant);
            this._apply();
        }
        disconnectedCallback() {
            if (this.button) this.button.removeEventListener('click', this._onMore);
            document.removeEventListener('variant:change', this._onVariant);
        }
        // Visible media = not variant-filtered (theme sets the native [hidden] attr on those).
        _cells() {
            return Array.prototype.slice.call(
                this.carousel.querySelectorAll('.product-gallery__media')
            ).filter(function (c) { return !c.hasAttribute('hidden'); });
        }
        _apply() {
            var cells = this._cells();
            var shown = cells;
            if (this.limit > 0 && !this.expanded && cells.length > this.limit) {
                shown = cells.slice(0, this.limit);
                cells.forEach(function (c, i) {
                    c.classList.toggle('agp-grid-hidden', i >= this.limit);
                }, this);
                if (this.button) this.button.hidden = false;
            } else {
                cells.forEach(function (c) { c.classList.remove('agp-grid-hidden'); });
                if (this.button) this.button.hidden = true;
            }
            // Full-width-last: the cell left alone on the last row spans both columns. Two orders
            // are in play and they are deliberately different:
            //   · the GATE reads the NATURAL order (DOM = Shopify's), so it answers "would the video
            //     be the stretched cell?" without depending on its own outcome. Reading the visual
            //     order here would make the class flip on and off forever, since applying it moves
            //     the video away from the end.
            //   · the SPAN follows the VISUAL order (CSS `order`, which the forced-slot rules set),
            //     so a moved video sits in a normal cell like any other media and whatever actually
            //     ends the grid is what gets stretched.
            var odd = shown.length % 2 === 1;
            var naturalLast = odd ? shown[shown.length - 1] : null;

            // "Force video to position N, only if the video is alone on the last row": the gallery
            // snippet scoped that <style> under .agp-video-slot-on. Decided here because this is
            // where the stretched cell is known — which keeps it true after variant filtering and
            // after "See more" reveals the rest, both of which change the count.
            if (this.dataset.videoSlot === '1') {
                var section = this.closest('.shopify-section');
                if (section) {
                    var isVideo = !!naturalLast && /video/.test(naturalLast.dataset.mediaType || '');
                    section.classList.toggle('agp-video-slot-on', isVideo);
                }
            }

            cells.forEach(function (c) { c.classList.remove('agp-grid-full'); });
            if (odd) {
                // Same ordering the grid itself uses: `order` first, DOM position as the tiebreak.
                var byVisual = shown.slice().sort(function (a, b) {
                    var oa = parseInt(getComputedStyle(a).order, 10) || 0;
                    var ob = parseInt(getComputedStyle(b).order, 10) || 0;
                    return oa - ob || shown.indexOf(a) - shown.indexOf(b);
                });
                byVisual[byVisual.length - 1].classList.add('agp-grid-full');
            }
        }
        _onMore() {
            this.expanded = true;
            this._apply();
        }
        _onVariant() {
            // Recompute after the theme finishes toggling [hidden] on filtered cells.
            requestAnimationFrame(this._apply);
        }
    });
}

/* ============================================================================
 * AGP Scroll Reveal — soft bottom sheet (Product Page v2)
 * Rendered at the top of the recommendations section when the reveal is enabled
 * for a device. Pins the PREVIOUS section (the product) on its last screen via a
 * negative sticky-top (top = viewportHeight − sectionHeight), and this section
 * rises up over it as a sheet. Adds, over the base reveal:
 *   - a per-device matchMedia gate (data-enable-desktop / -mobile) — inert on the
 *     other device, so "off" == a plain section;
 *   - a scrim injected over the pinned product, its opacity linked to scroll
 *     (0 → --agp-scrim-max as the sheet rises from the viewport bottom to --agp-peek);
 *   - drag-to-dismiss on the mobile handle and a ✕ on desktop, both gliding the
 *     product back into view (window.scrollTo).
 * The product height is dynamic (accordions, variant, image load) so we re-measure
 * on resize + ResizeObserver. Self-cleans on disconnect so the theme editor can
 * toggle settings (re-render → disconnect → reconnect). See the SSR shell in
 * sections/agp-product-recommendations.liquid + the CSS in assets/agapee.css.
 * ============================================================================ */
if (window.customElements && !customElements.get('agp-scroll-reveal')) {
    customElements.define('agp-scroll-reveal', class extends HTMLElement {
        connectedCallback() {
            this.section = this.closest('.shopify-section');
            // Target = the previous SECTION sibling (the product section it slides over).
            var prev = this.section ? this.section.previousElementSibling : null;
            while (prev && !prev.classList.contains('shopify-section')) {
                prev = prev.previousElementSibling;
            }
            this.target = prev;
            if (!this.target || !this.section) return;

            this._update = this._update.bind(this);
            this._onScroll = this._onScroll.bind(this);
            this._evaluate = this._evaluate.bind(this);
            this._onDragStart = this._onDragStart.bind(this);
            this._onDragMove = this._onDragMove.bind(this);
            this._onDragEnd = this._onDragEnd.bind(this);
            this._onCloseClick = this._onCloseClick.bind(this);
            this._onTargetClick = this._onTargetClick.bind(this);
            this._raf = 0;

            this.chrome = this.section.querySelector('.agp-sheet-chrome');
            this.handle = this.section.querySelector('[data-sheet-handle]');
            this.closeBtn = this.section.querySelector('[data-sheet-close]');

            // Per-device gate — re-evaluate when the viewport crosses the breakpoint.
            this._mq = window.matchMedia('(min-width: 1000px)');
            if (this._mq.addEventListener) this._mq.addEventListener('change', this._evaluate);
            else if (this._mq.addListener) this._mq.addListener(this._evaluate);

            this._evaluate();
        }

        disconnectedCallback() {
            if (this._mq) {
                if (this._mq.removeEventListener) this._mq.removeEventListener('change', this._evaluate);
                else if (this._mq.removeListener) this._mq.removeListener(this._evaluate);
            }
            this._disarm();
        }

        // Enabled for the current breakpoint?
        _deviceOn() {
            return this._mq.matches ? this.dataset.enableDesktop === '1' : this.dataset.enableMobile === '1';
        }

        _evaluate() {
            var on = this._deviceOn();
            if (on && !this._armed) this._arm();
            else if (!on && this._armed) this._disarm();
        }

        _arm() {
            this._armed = true;
            if (!this.scrim) {
                var cs = getComputedStyle(this.section);
                this._scrimMax = parseFloat(cs.getPropertyValue('--agp-scrim-max')) || 0;
                this.scrim = document.createElement('div');
                this.scrim.className = 'agp-sheet-scrim';
                this.scrim.style.setProperty('--agp-scrim-rgb', (cs.getPropertyValue('--agp-scrim-rgb') || '0 0 0').trim());
                // NB: stays pointer-events:none (CSS default) until the sheet is actually at the
                // peek — it covers the whole product section, so leaving it clickable would
                // swallow every tap on the gallery, accordions and buy buttons. Toggled in _onScroll.
                this.scrim.addEventListener('click', this._onCloseClick);
                this.target.appendChild(this.scrim);
            }
            this.target.style.zIndex = '0'; // product paints below the sheet (z-index:1)
            this.header = document.querySelector('.header');
            // The pinned product is position:sticky (a stacking context), so the fixed ATC inside
            // it is trapped BELOW the sheet — i.e. the sheet covers the bar by default. To keep
            // the bar on top, move it out to <body> ("Fixed Add to cart bar" setting, mobile).
            if (!this._mq.matches && this.dataset.atcOnTop === '1') this._detachAtc();
            else this._reattachAtc();
            this._update();
            if (window.ResizeObserver && !this._ro) {
                this._ro = new ResizeObserver(this._update);
                this._ro.observe(this.target);
                // Also watch the recs content: for the complementary source it is lazy-loaded
                // (and images settle later), so the grid's height — and therefore the spacer /
                // hold range — is only known after the fetch.
                var content = this.section.querySelector('product-recommendations') || this.section.lastElementChild;
                if (content) this._ro.observe(content);
            }
            // Spacer AFTER the sheet: sticky can only hold for as long as its containing block
            // has room below it, and the recs are the last section — so without this the sheet
            // could never hold at the peek. Its height == the grid's inner scroll distance, so
            // the page scroll spent here maps 1:1 to scrolling the grid inside the sheet.
            if (!this.spacer) {
                this.spacer = document.createElement('div');
                this.spacer.className = 'agp-sheet-spacer';
                this.spacer.setAttribute('aria-hidden', 'true');
                this.section.after(this.spacer);
            }
            this._measure();
            window.addEventListener('resize', this._update, { passive: true });
            window.addEventListener('scroll', this._onScroll, { passive: true });
            if (this.handle) this.handle.addEventListener('pointerdown', this._onDragStart);
            if (this.closeBtn) this.closeBtn.addEventListener('click', this._onCloseClick);
            this.target.addEventListener('click', this._onTargetClick);
            this._onScroll();
        }

        // Document position of the sheet's FLOW box. Measured from the spacer — a static element
        // right after the sheet, so it is never displaced by the sticky shift (the sheet's own
        // rect/offsetTop both move once it sticks, which would make the hold drift and restart).
        _flowTop() {
            if (this.spacer) {
                return this.spacer.getBoundingClientRect().top + (window.scrollY || window.pageYOffset || 0) - this.section.offsetHeight;
            }
            return this.section.getBoundingClientRect().top + (window.scrollY || window.pageYOffset || 0);
        }
        // Size the spacer to the grid's inner scroll distance and record where the hold starts.
        _measure() {
            if (!this._armed || !this.spacer) return;
            var rest = this._restTop();
            var inner = this.section.scrollHeight - this.section.clientHeight;
            if (inner < 0) inner = 0;
            this._inner = inner;
            this._measuredScrollH = this.section.scrollHeight;
            this.spacer.style.height = inner + 'px';
            this._stickY = this._flowTop() - rest; // page scroll at which the sheet starts holding
        }

        _disarm() {
            this._armed = false;
            if (this._ro) { this._ro.disconnect(); this._ro = null; }
            window.removeEventListener('resize', this._update);
            window.removeEventListener('scroll', this._onScroll);
            if (this._raf) { cancelAnimationFrame(this._raf); this._raf = 0; }
            if (this.handle) this.handle.removeEventListener('pointerdown', this._onDragStart);
            if (this.closeBtn) this.closeBtn.removeEventListener('click', this._onCloseClick);
            if (this.target) this.target.removeEventListener('click', this._onTargetClick);
            this._endDrag();
            this._reattachAtc();
            if (this.spacer) { this.spacer.remove(); this.spacer = null; }
            if (this.section) { this.section.style.transform = ''; this.section.style.transition = ''; this.section.scrollTop = 0; }
            if (this.scrim) { this.scrim.remove(); this.scrim = null; }
            if (this.target) {
                this.target.style.position = '';
                this.target.style.top = '';
                this.target.style.zIndex = '';
            }
        }

        // The line the sheet ACTUALLY sticks at — its used sticky `top`, i.e. the resolved
        // --agp-rest. Use this (not _restTop()) to decide "is it deployed?": _restTop() is
        // recomputed live from the header, which shrinks once solid, so the two disagree while
        // the sheet is parked at the value --agp-rest held when it was last written.
        _stickyLine() {
            var t = parseFloat(getComputedStyle(this.section).top);
            return isNaN(t) ? this._restTop() : t;
        }

        // The sheet's rest line = the peek measured BELOW the sticky header, so the product
        // strip shows in full beneath the (solid) header instead of being eaten by it.
        _restTop() {
            var peek = parseFloat(getComputedStyle(this.section).getPropertyValue('--agp-peek'));
            if (isNaN(peek)) peek = 0;
            var headBottom = this.header ? Math.max(0, this.header.getBoundingClientRect().bottom) : 0;
            // Clamp so the sheet always keeps at least ~96px in view (a huge peek would
            // otherwise push the rest line past the viewport and break the scrim ramp).
            return Math.min(peek + headBottom, window.innerHeight - 96);
        }

        // Pin the product's LAST screen (negative top). Peek does NOT change the pin —
        // the product stays bottom-aligned; the peek only maps the scrim + the rest.
        _update() {
            if (!this._armed) return;
            var h = this.target.offsetHeight;
            var vh = window.innerHeight;
            this.target.style.position = 'sticky';
            this.target.style.top = (h > vh ? (vh - h) : 0) + 'px';
            // Refine the sheet's stop line from the live header height (CSS has a fallback).
            this.section.style.setProperty('--agp-rest', this._restTop() + 'px');
            this._measure();
        }

        // Three phases, all driven by ordinary page scroll (no lock):
        //   1. RISE   — the sheet rises over the pinned product; the scrim fades in.
        //   2. HOLD   — it sticks at the peek; the page scroll spent over the spacer is mapped
        //               to the grid's inner scrollTop, so the products scroll INSIDE the sheet.
        //   3. RELEASE— the grid is exhausted, sticky lets go, the sheet scrolls away → footer.
        _onScroll() {
            if (this._raf) return;
            var self = this;
            this._raf = requestAnimationFrame(function () {
                self._raf = 0;
                if (!self._armed) return;
                // The recs body is lazy-loaded (and images settle), so the grid can grow after
                // the first measure — re-measure whenever its scroll height changes.
                if (self.section.scrollHeight !== self._measuredScrollH) self._measure();
                var vh = window.innerHeight;
                var rest = self._restTop();
                var top = self.section.getBoundingClientRect().top;
                if (self.scrim) {
                    var p = (vh - top) / ((vh - rest) || 1);
                    p = p < 0 ? 0 : (p > 1 ? 1 : p);
                    self.scrim.style.setProperty('--agp-scrim-o', (p * self._scrimMax).toFixed(3));
                    // Only tap-to-close while the sheet is actually deployed at the peek; the rest
                    // of the time the scrim must not intercept taps on the product beneath it.
                    // Compare with the line it really sticks at (see _stickyLine) — the live
                    // _restTop() shrinks with the solidified header and would never match.
                    self.scrim.style.pointerEvents = (top <= self._stickyLine() + 2) ? 'auto' : 'none';
                }
                var d = (window.scrollY || window.pageYOffset || 0) - (self._stickY || 0);
                if (d < 0) d = 0; else if (d > self._inner) d = self._inner;
                self.section.scrollTop = d;
            });
        }

        // ---- Drag the sheet down to close ----
        // The sheet is a bounded box (capped to the viewport below the peek, overflow hidden), so
        // it can be physically dragged: translateY follows the finger 1:1 and the scrim fades with
        // it. Released past the threshold it slides out and closes; short of it, it snaps back;
        // a tap (no movement) closes too.
        _onDragStart(e) {
            this._dragging = true;
            this._dragId = e.pointerId;
            this._dragStartY = e.clientY;
            this._dragDy = 0;
            this._sheetH = this.section.getBoundingClientRect().height || window.innerHeight;
            this.section.style.transition = 'none';
            try { this.handle.setPointerCapture(e.pointerId); } catch (err) {}
            this.handle.addEventListener('pointermove', this._onDragMove);
            this.handle.addEventListener('pointerup', this._onDragEnd);
            this.handle.addEventListener('pointercancel', this._onDragEnd);
        }
        _onDragMove(e) {
            if (!this._dragging) return;
            e.preventDefault();
            var dy = e.clientY - this._dragStartY;
            if (dy < 0) dy = 0; // downward only — the sheet can't be dragged above its rest line
            this._dragDy = dy;
            this.section.style.transform = 'translateY(' + dy + 'px)';
            if (this.scrim) {
                var ratio = 1 - Math.min(1, dy / this._sheetH);
                this.scrim.style.setProperty('--agp-scrim-o', (ratio * this._scrimMax).toFixed(3));
            }
        }
        _onDragEnd(e) {
            if (!this._dragging) return;
            var dy = this._dragDy || 0;
            this._endDrag();
            if (e && e.type === 'pointercancel') { this._snapBack(); return; }
            if (dy < 8) { this.section.style.transform = ''; this._close(); return; } // tap
            if (dy > Math.min(120, this._sheetH * 0.25)) this._dragClose();
            else this._snapBack();
        }
        // Slide the sheet the rest of the way out, then settle the page at the closed position
        // (sheet just below the fold) and drop the transform — visually continuous.
        _dragClose() {
            var s = this.section, self = this;
            var remaining = Math.max(0, this._sheetH - (this._dragDy || 0));
            var done = false;
            var settle = function () {
                if (done) return; done = true;
                s.removeEventListener('transitionend', settle);
                s.style.transition = '';
                window.scrollTo(0, self._closedScroll());
                s.style.transform = '';
                if (self.scrim) { self.scrim.style.setProperty('--agp-scrim-o', '0'); self.scrim.style.pointerEvents = 'none'; }
            };
            s.addEventListener('transitionend', settle);
            s.style.transition = 'transform .22s ease-out';
            s.style.transform = 'translateY(' + (this._dragDy + remaining) + 'px)';
            setTimeout(settle, 320); // fallback if transitionend doesn't fire
        }
        _snapBack() {
            var s = this.section, self = this;
            s.style.transition = 'transform .18s ease-out';
            s.style.transform = '';
            var clear = function () { s.style.transition = ''; s.removeEventListener('transitionend', clear); };
            s.addEventListener('transitionend', clear);
            if (this.scrim) this.scrim.style.setProperty('--agp-scrim-o', (this._scrimMax || 0).toFixed(3));
            setTimeout(function () { self._onScroll(); }, 200);
        }
        _endDrag() {
            this._dragging = false;
            if (this.handle) {
                this.handle.removeEventListener('pointermove', this._onDragMove);
                this.handle.removeEventListener('pointerup', this._onDragEnd);
                this.handle.removeEventListener('pointercancel', this._onDragEnd);
                try { this.handle.releasePointerCapture(this._dragId); } catch (err) {}
            }
            this._dragId = null;
        }

        // Where "closed" sits when we're not going to the top of the page: the sheet CLEAR of the
        // fold, not balanced on it. flowTop − vh would put its edge exactly at the viewport bottom,
        // where the slightest scroll reveals it again and it doesn't read as closed, so add a third
        // of a screen of clearance.
        _closedScroll() {
            var y = this._flowTop() - window.innerHeight - Math.round(window.innerHeight / 3);
            return y < 0 ? 0 : y;
        }

        _onCloseClick(e) { e.preventDefault(); this._close(); }

        // Click on the dimmed product while the sheet is PARTIALLY deployed: the scrim is
        // pointer-events:none there, so the click reaches the content and does its normal thing —
        // and we additionally dismiss the sheet. Fully deployed, the scrim is pointer-events:auto,
        // so the click never reaches the content and only closes (handled by _onCloseClick).
        _onTargetClick(e) {
            if (!this._armed || !this.scrim) return;
            if (e.target === this.scrim) return; // fully deployed — the scrim's own handler closed it
            // The fixed ATC bar floats above the page rather than being dimmed background:
            // adding to cart shouldn't also scroll the sheet away.
            if (e.target.closest && e.target.closest('.agp-fixed-atc')) return;
            var o = parseFloat(this.scrim.style.getPropertyValue('--agp-scrim-o')) || 0;
            if (o <= 0.001) return; // sheet isn't showing — ordinary page, leave the click alone
            this._close();
        }

        // Close = reverse the open: scroll up so the recs descend to just below the fold and the
        // product is fully shown again (the mirror of the opening scroll) — never a jump to top.
        _close() {
            var reduce = !window.matchMedia('(prefers-reduced-motion: no-preference)').matches;
            var animate = this.dataset.closeAnimate === '1' && !reduce;
            // Where closing lands, chosen per device: either the top of the page, or the mirror of
            // the opening scroll — back to where the sheet sits just below the fold (its flow top
            // == viewport bottom), fully re-showing the product.
            var toTop = this._mq.matches
                ? this.dataset.closeTopDesktop === '1'
                : this.dataset.closeTopMobile === '1';
            // "Scroll to top" only applies once the sheet is actually deployed at the peek. From a
            // partial reveal, jumping to the top of the page is jarring, so just reverse the reveal.
            if (toTop && this.section.getBoundingClientRect().top > this._stickyLine() + 2) toTop = false;
            var target = toTop ? 0 : this._closedScroll();
            if (target < 0) target = 0;
            window.scrollTo({ top: target, behavior: animate ? 'smooth' : 'auto' });
        }

        // Move the mobile fixed ATC out of the pinned product's stacking context (to
        // <body>) so it stays above the sheet; restore it on disarm. The ATC controller
        // is self-contained (its own form + document-level events), so re-parenting is safe.
        _detachAtc() {
            if (this._atc || !this.target) return;
            var atc = this.target.querySelector('.agp-fixed-atc');
            if (!atc || atc.parentNode === document.body) return;
            this._atc = atc;
            this._atcParent = atc.parentNode;
            this._atcNext = atc.nextSibling;
            document.body.appendChild(atc);
        }
        _reattachAtc() {
            if (this._atc && this._atcParent) {
                this._atcParent.insertBefore(this._atc, this._atcNext);
            }
            this._atc = null; this._atcParent = null; this._atcNext = null;
        }
    });
}
